/**
 * English localization
 */
export default {
  pipeline: {
    constructor: {
      title: 'Kanban Constructor',
      subtitle: 'Configure funnel stages and mandatory tasks for each stage',
      reset: 'Reset',
      save: 'Save funnel',
      saved: 'Funnel saved',
      color: 'Color',
      newStage: 'New stage',
      stageName: 'Stage name',
      stageNamePlaceholder: 'e.g. Presentation Scheduled',
      stageType: 'Stage type',
      terminalHint:
        '“Won” and “Lost” are final stages that close the deal (won / lost with a reason).',
      createStage: 'Create stage',
      typeNormal: 'Normal',
      typeWon: 'Won',
      typeLost: 'Lost',
      tasksCount: '{n} tasks',
      wipLimit: 'Card limit (WIP)',
      mandatory: 'Mandatory',
      optional: 'Optional',
      overdueIn: 'Overdue in {d}',
      nDays: '{n}d',
      nHours: '{n}h',
      addTask: 'Add task',
      addStage: 'Add stage',
      deleteStage: {
        title: 'Delete stage “{name}”?',
        protectedTitle: 'Stage “{name}” can’t be deleted',
        protectedText: 'This is a system funnel stage — it’s protected from deletion and moving.',
        protectedNote:
          'The start stage “Unsorted” and the final “Won” / “Lost” stages are protected to keep the funnel valid.',
        emptyText:
          'This stage has no active deals — nothing to move. The stage and its task templates will be deleted.',
        reassignText:
          'Stage “{name}” has {count} active deal(s). Pick a stage to move them to — the stage and its task templates will be deleted permanently.',
        moveLabel: 'Move {count} deal(s) to stage',
        selectPlaceholder: 'Select a stage',
        selectFirst: 'Select a stage first',
        dealsShort: '{n} deal | {n} deals',
        summary: '{count} deal(s) will move to “{target}”',
        templatesNote: 'The stage’s task templates ({n}) will be deleted.',
        reassignTemplatesNote:
          'The stage’s task templates ({n}) will be deleted. Tasks already created on deals stay in their history.',
        confirm: 'Delete stage',
        understood: 'Got it',
        movedToast: '{count} deal(s) moved to “{target}”',
        deletedToast: 'Stage deleted',
      },
      loadError: 'Failed to load the funnel',
      saveError: 'Failed to save changes',
    },
    taskEditor: {
      title: 'Stage task',
      taskName: 'Task name',
      taskNamePlaceholder: 'e.g. Agree on budget and timeline',
      mandatory: 'Mandatory task',
      mandatoryHint: 'Blocks moving the deal to the next stage until completed',
      deadline: 'Due',
      noDeadline: 'No due',
      afterDeadline: 'After period',
      hours: 'hours',
      days: 'days',
      afterEnter: 'after entering the stage',
      priority: 'Priority',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      save: 'Save task',
    },
    blocked: {
      modalTitle: 'Transition blocked',
      heading: 'Complete the mandatory tasks first',
      body: 'To move the deal to “{stage}”, close the mandatory tasks of the current stage with a result comment:',
      tasksLine: 'Not all mandatory tasks are completed with a comment',
      openTask: 'Go to tasks',
      openDeal: 'Open the deal',
    },
  },
  common: {
    access: {
      forbiddenTitle: 'Section unavailable',
      forbiddenDescription:
        'Your role has no access to this section. If you need it for work, contact an administrator.',
      toDashboard: 'Go to dashboard',
      noAccessTitle: 'CRM access denied',
      noAccessDescription:
        'Your account does not belong to this application. Sign in with a work account or contact an administrator.',
      logout: 'Sign out',
    },
    desktopOnly: {
      title: 'This screen is meant for a computer',
      description:
        'The screen needs a wide monitor: a phone shows only a small part of it, and there is no way to work with it. Open the section on a computer, or on a tablet in landscape.',
      back: 'Back',
    },
    hints: {
      languageSettings: 'Change language here!',
    },
    unknownManager: 'Unknown manager',
    table: {
      selected: 'Selected: {count}',
      noData: 'No data to display',
      noDataHint: 'Try changing filters or adding new entries',
      actions: 'Actions',
    },
    actions: {
      create: 'Create',
      new: 'New',
      globalSearch: 'Search clients, deals, properties…',
      collapseSidebar: 'Collapse menu',
      openMenu: 'Open menu',
      loadMore: 'Load more',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      finish: 'Finish',
      search: 'Search',
      filter: 'Filter',
      reset: 'Reset',
      close: 'Close',
      add: 'Add',
      remove: 'Remove',
      view: 'View',
      download: 'Download',
      upload: 'Upload',
      export: 'Export',
      import: 'Import',
      print: 'Print',
      share: 'Share',
      retry: 'Retry',
      clear: 'Clear',
      apply: 'Apply',
      all: 'All',
      yes: 'Yes',
      no: 'No',
    },
    globalSearch: {
      groups: {
        clients: 'Clients',
        deals: 'Deals',
        properties: 'Properties',
      },
      hint: 'Start typing to search clients, deals and properties',
      minChars: 'Enter at least 2 characters',
      loading: 'Searching…',
      empty: 'Nothing found',
      emptyHint: 'Try a different query',
      error: 'Search failed',
      navHint: 'navigate',
      openHint: 'open',
      recent: 'Recent',
      newClient: 'New client',
      newDeal: 'New deal',
    },
    calendar: {
      today: 'Today',
      weekDays: {
        mon: 'Mon',
        tue: 'Tue',
        wed: 'Wed',
        thu: 'Thu',
        fri: 'Fri',
        sat: 'Sat',
        sun: 'Sun',
      },
      presets: {
        today: 'Today',
        last7Days: 'Last 7 days',
        thisMonth: 'This month',
      },
    },
    views: {
      table: 'Table',
      kanban: 'Kanban',
    },
    labels: {
      name: 'Name',
      description: 'Description',
      date: 'Date',
      status: 'Status',
      type: 'Type',
      price: 'Price',
      address: 'Address',
      city: 'City',
      country: 'Country',
      phone: 'Phone',
      email: 'Email',
      notes: 'Notes',
      comments: 'Comments',
      createdAt: 'Created At',
      updatedAt: 'Updated At',
      theme: 'Theme',
      lightTheme: 'Light Theme',
      darkTheme: 'Dark Theme',
      notifications: 'Notifications',
      noResults: 'No results found',
      search: 'Search',
      yes: 'Yes',
      no: 'No',
      other: 'Other',
    },
    messages: {
      loading: 'Loading...',
      noData: 'No data',
      error: 'An error occurred',
      success: 'Success',
      confirmDelete: 'Are you sure you want to delete?',
      requiredField: 'This field is required',
      invalidEmail: 'Invalid email',
      invalidPhone: 'Invalid phone number',
    },
    errors: {
      loadingFailed: 'Loading failed',
      saveFailed: 'Save failed',
      deleteFailed: 'Delete failed',
      networkError: 'Network error',
      unauthorized: 'Authorization required',
      forbidden: 'Access denied',
      notFound: 'Not found',
      serverError: 'Server error',
    },
    navigation: {
      partnerCompanies: 'Partner companies',
      notifications: 'Notifications',
      home: 'Home',
      dashboard: 'Dashboard',
      profile: 'Profile',
      clients: 'Clients',
      agents: 'Agents',
      agentsRegistry: 'Agent registry',
      moderation: 'Moderation',
      analytics: 'Analytics',
      properties: 'Properties',
      projectsSection: 'Project management',
      projectsList: 'Projects',
      projectsImport: 'Project import',
      deals: 'Deals',
      calendar: 'Calendar',
      reports: 'Reports',
      settings: 'Settings',
      tools: 'Tools',
      calls: 'Calls',
      documents: 'Documents',
      system: 'System',
      managerAnalytics: 'Manager Analytics',
      routingRules: 'Routing',
      pipelineConstructor: 'Funnel constructor',
      personalSettings: 'Personal settings',
      administration: 'Administration',
    },
    pagination: {
      showing: 'Showing {count} of {total}',
      page: 'Page {current} of {total}',
      previous: 'Previous',
      next: 'Next',
    },
  },
  history: {
    loading: 'Loading history…',
    loadMore: 'Load more',
    taskBadge: 'Task',
    openTasks: 'Go to tasks',
    taskResult: 'Result: {result}',
    filters: {
      action: 'Action type',
      allActions: 'All actions',
      actor: 'Author',
      allActors: 'All authors',
      period: 'Period',
    },
    empty: {
      title: 'No history yet',
      description: 'There are no changes yet',
    },
    actions: {
      created: 'Created',
      updated: 'Updated',
      deleted: 'Deleted',
      manager_changed: 'Manager changed',
      note_added: 'Note added',
      contact_added: 'Contact added',
      contact_removed: 'Contact removed',
      task_created: 'Task created',
      task_updated: 'Task updated',
      task_completed: 'Task completed',
      task_auto_closed: 'Task closed automatically',
      task_deleted: 'Task deleted',
      file_uploaded: 'File uploaded',
      file_downloaded: 'File downloaded',
      stage_changed: 'Stage changed',
      status_changed: 'Status changed',
      closed: 'Closed',
      commission_paid: 'Commission paid',
      comment_added: 'Comment added',
    },
  },
  clients: {
    contactsList: {
      primary: 'Primary',
      empty: 'No contacts',
      add: 'Add contact',
      valuePlaceholder: 'Value',
    },
    title: 'Clients',
    subtitle: 'Client database management',
    listSubtitle: 'Client database management · {count} contacts',
    notAssigned: 'Unassigned',
    bulkDelete: { confirm: 'Delete {count} clients?' },
    newClient: 'New Client',
    searchPlaceholder: 'Search by name, phone, email...',
    create: 'Create Client',
    edit: 'Edit Client',
    details: 'Client Details',
    list: 'Client List',
    noManager: 'No Manager',
    stats: {
      total: 'Total Clients',
      newThisMonth: 'New This Month',
      activeDeals: 'Active Deals',
      allTime: 'all time',
      vsPrevMonth: 'vs last month',
      withDeal: 'With deal',
      withDealCaption: 'active deals',
      unassigned: 'Unassigned',
      unassignedCaption: 'need distribution',
    },
    filters: {
      title: 'Filters',
      stage: 'Stage',
      allStages: 'All stages',
      allSources: 'All sources',
      sourceTree: {
        utmSource: 'Tag: {value}',
      },
      allCities: 'All cities',
      allLanguages: 'All languages',
      allDealStatuses: 'All',
      allManagers: 'All managers',
      withDeal: 'With deal',
      withoutDeal: 'Without deal',
      dealStatus: 'Deal status',
      manager: 'Manager',
      allAgents: 'All agents',
      agent: 'Agent',
      createdAt: 'Created date',
      createdAtPlaceholder: 'Select period',
      active: 'Active filters',
      clearAll: 'Clear all',
      reset: 'Reset filters',
      tag: 'Tag',
      allTags: 'All tags',
      archiveReason: 'Archive reason',
      allArchiveReasons: 'All reasons',
    },
    presets: {
      aria: 'Clients list preset',
      all: 'All',
      mine: 'Mine',
      unassigned: 'Unassigned',
      agents: 'Agent leads',
      duplicates: 'Duplicates',
    },
    duplicatesBlock: {
      title: 'Agent duplicates',
      viewAll: 'All duplicates ({n})',
    },
    export: {
      csv: 'CSV (table)',
      xlsx: 'Excel (XLSX)',
    },
    import: {
      uploadFile: 'Upload file',
      downloadTemplate: 'Download CSV template',
      invalidFormat: 'Only CSV and XLSX files are supported',
      failedToast: 'Failed to import clients',
      resultTitle: 'Import result',
      summary: 'Rows processed: {total}',
      created: 'Created: {count}',
      merged: 'Enriched: {count}',
      skipped: 'Skipped: {count}',
      failed: 'Failed: {count}',
      errorsTitle: 'Row errors',
      line: 'Row {line}',
    },
    table: {
      client: 'Client',
      contact: 'Contact',
      interest: 'Goal',
      budget: 'Budget',
      timeline: 'Timeline',
      manager: 'Manager',
      createdAt: 'Created At',
    },
    actions: {
      changeStatus: 'Change Status',
      addClient: 'Add Client',
      createClient: 'Create Client',
      retry: 'Retry',
      addNote: 'Add Note',
      bulkUpdate: 'Bulk Update',
      assignManager: 'Assign Manager',
    },
    bulkUpdate: {
      title: 'Bulk Update Clients',
      description: 'Selected clients: {count}. Specify fields to update.',
      newStage: 'New Stage',
      newManager: 'New Manager',
      keepCurrent: 'Keep current',
      archiveClients: 'Archive clients',
      success: 'Updated clients: {count}',
      error: 'Bulk update failed',
      noChanges: 'Select at least one field to update',
    },
    archive: {
      archive: 'Archive',
      unarchive: 'Unarchive',
      confirmArchive: 'Are you sure you want to archive this client?',
      cascadeHint:
        "The client's active deals will be closed as Closed Lost, and open tasks for the client and their deals will be removed.",
      revivedOnCreate: 'A client with these contacts was archived — the card has been restored',
      confirmUnarchive: 'Are you sure you want to unarchive this client?',
      successArchived: 'Client archived',
      successUnarchived: 'Client unarchived',
      error: 'Failed to archive client',
      archivedAt: 'Archived',
      archivedBadge: 'Archived',
      reason: 'Archive reason',
      reasonPlaceholder: 'Specify the archive reason...',
      reasonRequired: 'Please specify the archive reason',
      reasonSelectPlaceholder: 'Select a reason',
      comment: 'Comment',
      commentPlaceholder: 'Closing details (optional)...',
    },
    tags: {
      add: 'Add tag',
      remove: 'Remove tag',
    },
    // DEV-2477. Client's local time in the header of the client and deal cards.
    localTime: {
      label: "client's time",
      day: 'Good time to call',
      early: 'Early: better to message than to call',
      late: 'Late: better to message than to call',
      night: "Night at the client's: do not disturb",
      unknown: "Client's time unknown",
      unknownHint: "The time zone comes from the client's city in the directory",
      source: {
        city: 'zone from the city',
        device: "zone from the client's device in their request",
        country: 'zone from the country',
        phone: 'zone from the phone number',
      },
    },
    detail: {
      noContacts: 'No contacts specified',
      preferredContactMethod: 'Preferred Contact Method',
      firstContactDate: 'First Contact Date',
      responsibleManager: 'Responsible Manager',
      call: 'Call',
      write: 'Message',
      tasks: {
        title: 'Tasks',
        add: 'Add task',
        edit: 'Edit task',
        empty: 'No tasks yet',
        assigneeDefault: 'Client responsible manager (default)',
        confirmDelete: 'Delete this task?',
      },
      agent: 'Agent',
      agentSource: {
        channel: 'Acquisition channel',
        sourceTypes: {
          Agent: 'Agent',
          Direct: 'Direct',
          Marketing: 'Marketing',
          Referral: 'Referral',
          Other: 'Other',
        },
        company: 'Company',
        openCompany: 'Open company',
        model: 'Model',
        special: 'Special',
        duplicate: 'Duplicate',
        duplicateStatuses: {
          not_checked: 'Not checked',
          duplicate_pending: 'Possible duplicate',
          duplicate_confirmed: 'Duplicate confirmed',
          rejected: 'Rejected',
        },
        comment: 'Agent comment',
        // DEV-1918. Acquisition channel / agent (ClientSourceField).
        gateHint: 'Changed by ADMIN, HOS, CEO',
        noAgent: 'Agent not set',
        setAgent: 'Set agent',
        mismatch: 'channel mismatch',
        mismatchHint:
          'The client has an agent, but the acquisition channel is not "Agent". Switching the channel to "Agent" fixes the mismatch.',
        changeAgent: 'Change agent',
        openAgent: 'Open agent',
        details: 'Details',
        registeredAt: 'Registered by agent',
        duplicateReason: 'Duplicate reason',
        panelRequest: 'Panel request',
        saving: 'Saving…',
        cancelHint: 'restores the previous channel, nothing is saved',
        picker: {
          placeholder: 'Search agent by name or phone…',
          empty: 'No agents found',
          error: 'Failed to load agents',
          scopeHint: 'Showing agents in your scope',
        },
        confirm: {
          assignTitle: 'Assign agent?',
          assignDescription:
            '{agent} will become the client’s agent, acquisition channel — "Agent".',
          assign: 'Assign',
          assignImpact: 'Agent attribution and reward will move to {n} open deals.',
          changeTitle: 'Change agent?',
          changeDescription: 'The agent will change: {old} → {new}.',
          change: 'Change',
          changeImpact: 'Attribution of {n} open deals will move to the new agent.',
          removeTitle: 'Remove agent?',
          removeDescription:
            '{agent} will no longer be the client’s agent, acquisition channel becomes "{channel}".',
          remove: 'Remove agent',
          removeImpact:
            'Agent attribution and reward will be removed from {n} open deals; no reward will accrue on them.',
          noDeals: 'No open deals.',
          sync: 'The client’s open deals will be synchronized.',
          dealsError: 'Failed to count open deals.',
          dealsRetry: 'Recount',
        },
        toast: {
          assigned: 'Agent assigned',
          changed: 'Agent changed',
          removed: 'Agent removed',
          channelUpdated: 'Acquisition channel updated',
          syncPending: 'Deals will update within a minute',
        },
      },
      // DEV-1918. Client card v2: team & attribution headings and inner cards.
      attribution: { leads: 'LEADS', broughtBy: 'BROUGHT BY', heardFrom: 'HEARD FROM' },
      entryPoints: 'Entry points',
      loadError: 'Failed to load client',
      inCrmSince: 'in CRM since {date}',
      // DEV-2477. Creation date in the manager's time or, on click, the client's.
      createdZone: {
        yours: 'your time',
        client: "client's time",
        toClient: "Show in the client's time",
        toYours: 'Show in your time',
      },
      responsibleShort: 'Resp.: {name}',
      note: 'Note',
      focus: { title: 'Focus', overdue: '{n} overdue', more: '{n} more' },
      qualification: {
        title: 'Qualification',
        subtitle: 'budget, goal, timeline, criteria',
        financing: 'Financing',
      },
      profile: {
        title: 'Profile',
        meta: 'Created {created} · updated {updated} · id {id}',
        copyId: 'Copy id',
        copied: 'Copied',
      },
      team: {
        title: 'Team & attribution',
        notAssigned: 'Not assigned',
        assignSelf: 'Assign me',
        impact:
          'The responsible manager changes on the client — all active deals of this client ({n}) move to the new manager.',
        managersHistory: 'Managers history',
      },
      tasksCard: {
        updateError: 'Failed to update the task',
        add: 'Task',
        mandatory: 'Mandatory',
        noDue: 'no due date',
        client: 'client',
        showAll: 'show all ({n})',
        collapse: 'collapse',
        completed: 'completed ({n})',
        empty: 'No tasks',
        openDeal: 'Open deal',
      },
      dealsCard: {
        title: 'Deals',
        add: 'Deal',
        current: 'current',
        all: 'all ({n})',
        collapse: 'collapse',
        empty: 'No deals yet',
        mandatory: '{done}/{total} mandatory',
      },
      feed: {
        title: 'Feed',
        placeholder: 'Write a note…',
        important: 'Important',
        add: 'Add',
        note: 'note',
        interaction: 'interaction',
        more: 'more ({n})',
        empty: 'Nothing yet',
      },
      contactsCard: {
        empty: 'No contacts',
        add: 'Add',
        newsletter: 'Newsletter',
        telegramBot: 'Telegram bot',
        openChat: 'Write in {provider}',
        email: 'Send email',
        mismatch: '≠ on deal',
        mismatchHint:
          'Deal attribution is fixed at creation. Current client agent — {agent}. It can only be changed on the client card.',
        editAtClient: 'Change on client',
        channelAgent: 'Channel · agent',
        noPhone: 'No phone',
        budget: 'Budget',
        loadError: 'Failed to load contacts',
      },
      you: 'you',
      assignSelf: 'Assign me as responsible',
      assignSelfConfirmTitle: 'Take over this client?',
      assignSelfConfirmDescription:
        '{manager} is currently responsible. Make yourself the responsible manager?',
      currentDeal: 'Current Deal',
      expectedCommission: 'Expected Commission',
      paymentStatus: 'Payment Status',
      fundingType: 'Funding Type',
      documents: 'Documents',
      stageTasks: 'Stage Tasks',
      addTask: 'Add Task',
      pending: 'pending',
      noTasksForStage: 'No tasks for this stage',
      addFirstTask: 'Add first task',
      stageBlockedWarning: 'Pending tasks block transition to the next stage',
      taskDescription: 'Task description',
      taskDescriptionPlaceholder: 'Enter task description...',
      taskPriority: 'Priority',
      taskDueDate: 'Due date',
      changeHistory: 'Change History',
      noChanges: 'No history records',
      loadMore: 'Load more',
      files: 'Files',
    },
    changeAction: {
      CREATED: 'Client created',
      UPDATED: 'Data updated',
      STAGE_CHANGED: 'Stage changed',
      STATUS_CHANGED: 'Status changed',
      TASK_CREATED: 'Task created',
      TASK_UPDATED: 'Task updated',
      TASK_COMPLETED: 'Task completed',
      TASK_DELETED: 'Task deleted',
      NOTE_ADDED: 'Note added',
      NOTE_UPDATED: 'Note updated',
      NOTE_DELETED: 'Note deleted',
      MANAGER_ASSIGNED: 'Manager assigned',
      CONTACT_ADDED: 'Contact added',
      CONTACT_REMOVED: 'Contact removed',
      DELETED: 'Client deleted',
    },
    stageRequirements: {
      title: 'Stage Requirements',
      remaining: 'remaining',
      allComplete: 'All complete',
      fill: 'Fill',
      warning: 'Fill all fields to advance to stage "{stage}"',
      readyToAdvance: 'Ready to advance to stage "{stage}"',
    },
    tabs: {
      overview: 'Overview',
      calls: 'Calls',
      files: 'Files',
      history: 'History',
      chats: 'Chats',
    },
    emptyState: {
      title: 'No clients found',
      noClients: 'Start by adding your first client',
      withFilters: 'Try changing filters to find clients',
    },
    form: {
      personalInfo: 'Personal Information',
      firstName: 'First Name',
      lastName: 'Last Name',
      dateOfBirth: 'Date of Birth',
      language: 'Language',
      contacts: 'Contacts',
      addContact: 'Add Contact',
      addContactPlaceholder: 'Add at least one contact',
      noContacts: 'No contacts added yet. Click the button above to add a contact.',
      contactType: 'Contact Type',
      contactValue: 'Value',
      preferredContact: 'Preferred Contact Method',
      clientStatus: 'Client Status',
      leadSource: 'Lead Source',
      // DEV-699. Optional responsible manager on client creation.
      managerOptionalAuto: 'Assign automatically',
      managerOptionalHint: 'If no manager is selected, the client will be assigned automatically',
      interests: 'Interests and Needs',
      propertyTypes: 'Property Types',
      locations: 'Locations',
      budget: 'Budget',
      budgetMin: 'Minimum',
      budgetMax: 'Maximum',
      currency: 'Currency',
      purchaseGoal: 'Purchase Goal',
      dealTimeline: 'Purchase Timeline',
      cityInterest: 'City of Interest',
      additionalCriteria: 'Additional Criteria',
      additionalInfo: 'Additional Information',
      comment: 'Comment',
      subscribeNewsletter: 'Subscribe to Newsletter',
      preview: 'Preview',
      selectCountry: 'Select country',
      selectStatus: 'Select status',
      selectSource: 'Select source',
      enterCity: 'Enter city',
      enterCityName: 'Enter city name',
      enterLocation: 'Enter location and press Enter',
      enterCriteria: 'Enter criteria and press Enter',
      from: 'From',
      to: 'To',
      notSpecified: 'Not specified',
      commentPlaceholder: 'Additional information about the client...',
      checkData: 'Check the entered data before creating',
      selectDate: 'Select date',
      errors: {
        required: 'This field is required',
        invalidEmail: 'Invalid email address',
        invalidPhone: 'Invalid phone number',
      },
    },
    wizard: {
      step1: 'Basics',
      step1Desc: 'Name, language, source',
      step2: 'Contacts',
      step2Desc: 'Phone, email, channels',
      step3: 'Budget',
      step3Desc: 'Goal, budget, finances',
      step4: 'Finish',
      step4Desc: 'Owner, tags',
      stepOf: 'Step {current} of {total}',
      subtitle: 'Fill the client card step by step',
      stepsCap: 'STEPS',
      responsible: 'Responsible manager',
      create: 'Create client',
    },
    propertyTypes: {
      apartment: 'Apartment',
      villa: 'Villa',
      townhouse: 'Townhouse',
      land: 'Land',
      commercial: 'Commercial',
    },
    contactTypes: {
      email: 'Email',
      phone: 'Phone',
      telegram: 'Telegram',
      whatsapp: 'WhatsApp',
      other: 'Other',
    },
    enums: {
      contactType: {
        phone: 'Phone',
        email: 'Email',
        whatsapp: 'WhatsApp',
        telegram: 'Telegram',
        other: 'Other',
      },
      propertyType: {
        apartments: 'Apartments',
        villas: 'Villas',
        commercial: 'Commercial',
      },
      purchaseGoal: {
        investment: 'Resale',
        living: 'Living',
        rental: 'Renting out',
      },
      dealTimeline: {
        urgent: 'Urgent',
        threeToSixMonths: '3–6 months',
        moreThanSixMonths: 'More than 6 months',
      },
      paymentStatus: {
        deposit: 'Deposit',
        advance: 'Advance Payment',
        fullPayment: 'Full Payment',
      },
      fundingType: {
        mortgage: 'Mortgage',
        installment: 'Installment',
        cash: 'Cash',
      },
      originEntityType: {
        article: 'Article',
        residentialComplex: 'Residential Complex',
      },
      priority: {
        low: 'Low',
        medium: 'Medium',
        high: 'High',
      },
    },
    // DEV-42: clients.stages and clients.kanban removed — client kanban is upraised.
  },
  properties: {
    title: 'Projects',
    objects: 'objects',
    addProperty: 'Add Property',
    addComplex: 'Add complex',
    addComplexHint: 'Complexes are created in the admin panel',
    listSubtitle: 'Real-estate catalog · {complexes} in catalog · {drafts} drafts',
    found: 'Found {count} properties',
    selected: 'Selected: {count} properties',
    searchPlaceholder: 'Search by complex name...',
    statusFilter: {
      all: 'All',
      published: 'Published',
      drafts: 'Drafts',
    },
    create: 'Create Property',
    edit: 'Edit Property',
    details: 'Property Details',
    list: 'Property List',
    views: {
      aria: 'View mode',
      grid: 'Grid',
      list: 'List',
      map: 'Map',
    },
    featureChips: {
      foreigners: 'For foreigners',
      pinkBook: 'Pink book',
      video: 'Video',
      docsChecked: 'Docs checked',
    },
    // DEV-1003: RC availability to agents (toggle in "Objects", admin/hos)
    agentAvailability: {
      badge: 'Available to agents',
      toggle: 'Available to agents',
      enabled: 'Project opened to agents',
      disabled: 'Project hidden from agents',
      error: 'Failed to change availability',
    },
    stats: {
      total: 'Total complexes',
      inSale: 'Units on sale',
      featured: 'Featured',
      developers: 'Developers',
      pending: 'pending',
      newThisMonth: 'New This Month',
    },
    sort: {
      name: 'By Name',
      priceAsc: 'Price: Low to High',
      priceDesc: 'Price: High to Low',
      area: 'By Area',
      date: 'By Date Added',
    },
    actions: {
      compare: 'Compare',
      clearFilters: 'Clear Filters',
      addFirst: 'Add First Property',
      downloadPresentation: 'Download PDF',
      generatePresentation: 'Generate PDF',
      generatingPresentation: 'Generating...',
      regeneratePresentation: 'Regenerate',
    },
    emptyState: {
      title: 'No properties found',
      subtitle: 'Try changing your search parameters or filters',
    },
    compare: {
      title: 'Compare Properties',
      instruction: 'Select 2 to 4 properties to compare',
    },
    confirm: {
      delete: 'Delete {count} properties?',
    },
    tags: {
      recommended: 'Recommended',
    },
    noResults: 'No properties found',
    roomsCount: '{count} room | {count} rooms',
    types: {
      apartment: 'Apartment',
      villa: 'Villa',
      townhouse: 'Townhouse',
      commercial: 'Commercial',
    },
    objectTypes: {
      OBJECT_TYPE_APARTMENT: 'Apartment',
      OBJECT_TYPE_STUDIO: 'Studio',
      OBJECT_TYPE_PENTHOUSE: 'Penthouse',
      OBJECT_TYPE_VILLA: 'Villa',
      OBJECT_TYPE_TOWNHOUSE: 'Townhouse',
      OBJECT_TYPE_DUPLEX: 'Duplex',
      OBJECT_TYPE_CONDOTEL: 'Condotel',
      OBJECT_TYPE_SHOPHOUSE: 'Shophouse',
      OBJECT_TYPE_OFFICE: 'Office',
      OBJECT_TYPE_RETAIL: 'Retail',
      OBJECT_TYPE_LAND: 'Land',
    },
    filters: {
      searchByName: 'Search by name...',
      projectType: 'Project Type',
      type: 'Type',
      city: 'City',
      priceFrom: 'Price from',
      priceTo: 'to',
      moreFilters: 'More Filters',
      hideFilters: 'Hide Filters',
      location: 'Location',
      cities: 'Cities',
      additional: 'Additional',
      featured: 'Featured',
      visibility: 'Visibility',
      projectRank: 'Project Rank',
      amenities: 'Amenities',
      features: 'Features',
      apply: 'Apply Filters',
      drawer: {
        title: 'Filters',
        reset: 'Reset',
        priceRange: 'Price, ₫',
        showResults: 'Show {count} complexes',
      },
      chips: {
        search: 'Search',
        type: 'Type',
        rooms: 'Rooms',
        rank: 'Rank',
        amenities: 'Amenities',
        features: 'Features',
      },
    },
    map: {
      complexesOnMap: '{count} complexes on map',
      sortByPrice: 'By price',
      searchThisArea: 'Search this area',
      loading: 'Loading map...',
    },
    card: {
      addToFavorites: 'Add to favorites',
      removeFromFavorites: 'Remove from favorites',
      addToCompare: 'Add to compare',
      from: 'from',
      priceOnRequest: 'Price on request',
      objectsCount: '{count} unit type | {count} unit types',
      amenitiesCount: '{count} amenity | {count} amenities',
      rooms: '{count} rooms',
      area: '{value} m²',
      floor: '{floor}/{total} fl.',
      year: '{year} yr',
      details: 'Details',
      downloadPresentation: 'Download presentation',
      generatePresentation: 'Generate presentation',
    },
    status: {
      available: 'Available',
      reserved: 'Reserved',
      sold: 'Sold',
      new: 'New',
      hot: 'Hot',
    },
    detail: {
      errorLoadingData: 'Failed to load property data',
      objectsInComplex: 'Objects in Complex',
      promos: 'Promotions',
      aboutObject: 'About the project',
      amenities: 'Amenities',
      highlights: 'Highlights',
      documentsCheck: 'Document verification',
      developer: 'Developer',
      files: 'Files & presentations',
      location: 'Location',
      downloadPresentation: 'Download presentation',
      // DEV-2249: on phones the project and developer descriptions are collapsed
      showFull: 'Show full text',
      collapse: 'Collapse',
    },
    unitsTable: {
      type: 'Type',
      rooms: 'Rooms',
      area: 'Area',
      available: 'Available',
      price: 'Price',
    },
    amenityLabels: {
      AMENITY_BAR: 'Bar',
      AMENITY_RESTAURANT: 'Restaurant',
      AMENITY_SPA: 'Spa',
      AMENITY_YOGA: 'Yoga',
      AMENITY_CONVENIENCE_STORE: 'Convenience store',
      AMENITY_SHOPPING_MALL: 'Shopping mall',
      AMENITY_SCHOOL: 'School',
      AMENITY_KINDERGARTEN: 'Kindergarten',
      AMENITY_COLLEGE: 'College',
      AMENITY_SWIMMING_POOL: 'Swimming pool',
      AMENITY_KIDS_PLAYGROUND: 'Kids playground',
      AMENITY_GYM: 'Gym',
      AMENITY_COMMUNITY_ROOM: 'Community room',
      AMENITY_JOGGING_PATH: 'Jogging path',
      AMENITY_BBQ: 'BBQ area',
      AMENITY_GREEN_AREA: 'Green area',
      AMENITY_TENNIS_COURT: 'Tennis court',
      AMENITY_KIDS_POOL: 'Kids pool',
      AMENITY_PARKING: 'Parking',
      AMENITY_CAFE: 'Cafe',
    },
    highlightLabels: {
      HIGHLIGHT_REPUTABLE_DEVELOPER: 'Reputable developer',
      HIGHLIGHT_INSTALLMENT_PLAN: 'Installment plan',
      HIGHLIGHT_FULL_LEGAL_GROUND: 'Full legal grounds',
      HIGHLIGHT_FREE_HIGH_APPRECIATION_POTENTIAL: 'High appreciation potential',
    },
    documentLabels: {
      DEVELOPERS_DOCUMENTS_CHECKING_CONSTRUCTION_PERMIT: 'Construction permit',
      DEVELOPERS_DOCUMENTS_CHECKING_LAND_USE_RIGHT_CERTIFICATE: 'Land use right certificate',
      DEVELOPERS_DOCUMENTS_CHECKING_BANK_LETTER_OF_GUARANTEE: 'Bank letter of guarantee',
      DEVELOPERS_DOCUMENTS_CHECKING_SALE_PERMIT: 'Sale permit',
      DEVELOPERS_DOCUMENTS_CHECKING_SALE_PERMIT_FOR_FOREIGNERS: 'Sale permit for foreigners',
    },
    quickInfo: {
      published: 'Published',
      hidden: 'Hidden',
      featured: 'Featured',
      minPrice: 'Min. Price',
      blocks: 'blocks',
      objectTypes: 'object types',
      handover: 'handover',
      promos: 'promos',
      objectTypesTitle: 'Object Types',
      created: 'Created',
      updated: 'Updated',
      developer: 'Developer',
      goToDeveloper: 'Go to Developer',
    },
    tabs: {
      overview: 'Overview',
      locationTab: 'Location',
      developerTab: 'Developer',
      documentsTab: 'Documents',
      description: 'Description',
      features: 'Features',
      amenities: 'Amenities',
      projectHighlights: 'Project Highlights',
      location: 'Location',
      noLocation: 'Location not specified',
      projectsCount: '{count} projects',
      since: 'Since {year}',
      rating: '{value} rating',
      allDeveloperProjects: 'All Developer Projects',
      verifiedDocuments: 'Verified Documents',
      noDocuments: 'Document information not available',
    },
  },
  files: {
    source: {
      upload: 'Upload',
    },
    dropOrClick: 'Drag files here',
    orClickToSelect: 'or click to select',
    uploading: 'Uploading...',
    loading: 'Loading files...',
    empty: 'No files uploaded',
    emptyDescription: 'Upload files by dragging them into the area above',
    fileName: 'File name',
    description: 'Description',
    descriptionPlaceholder: 'Short file description (optional)',
    uploadFile: 'Upload file',
    upload: 'Upload',
    download: 'Download',
    rename: 'Rename',
    delete: 'Delete',
    confirmDelete: 'Are you sure you want to delete this file?',
    uploadSuccess: 'File uploaded',
    uploadError: 'File upload failed',
    deleteSuccess: 'File deleted',
    deleteError: 'File deletion failed',
  },
  documents: {
    title: 'Documents',
    description: 'Agreement templates and PDF document generation for deals',
    templates: 'Templates',
    list: 'Documents',
    create: 'Create',
    filters: { status: 'Status' },
    status: { draft: 'Draft', ready: 'Ready' },
    columns: {
      title: 'Title',
      template: 'Template',
      date: 'Document date',
      size: 'Size',
      created: 'Created',
      status: 'Status',
    },
    actions: {
      generate: 'Generate document',
      download: 'Download',
      rename: 'Rename',
      delete: 'Delete',
    },
    empty: {
      title: 'No documents yet',
      description: 'Generate your first document from a template above',
      filtered: 'Nothing matches the selected filters',
    },
    generate: {
      title: 'New document',
      template: 'Template',
      deal: 'Deal',
      selectDeal: 'Select a deal',
      client: 'Client',
      selectClient: 'Select a client',
      amount: 'Amount',
      amountPlaceholder: 'Enter amount',
      amountInvalid: 'Amount must be positive',
      noTemplates: 'Templates are unavailable — try refreshing the page',
      date: 'Agreement date',
      submit: 'Generate',
      success: 'Document generated',
      error: 'Failed to generate document',
    },
    rename: {
      title: 'Rename document',
      name: 'Title',
      success: 'Document renamed',
      error: 'Failed to rename document',
    },
    confirmDelete: 'Delete this document? The file will be permanently removed.',
    deleteSuccess: 'Document deleted',
    deleteError: 'Failed to delete document',
    downloadError: 'Failed to download file',
    loadError: 'Failed to load documents',
    templatesError: 'Failed to load templates',
    retry: 'Retry',
  },
  deals: {
    // DEV-210. Next task due date on the kanban card.
    nextTask: {
      today: 'today',
      tomorrow: 'tomorrow',
      inDays: 'in {n} {unit}',
      overdue: 'overdue by {n} {unit}',
      dayOne: 'day',
      dayFew: 'days',
      dayMany: 'days',
    },
    title: 'Deals',
    createDeal: 'Create Deal',
    searchPlaceholder: 'Search by title or comment…',
    funnelSummary: 'Sales funnel · {count} active · {amount} in progress',
    views: {
      aria: 'View: board or list',
      board: 'Board',
      list: 'List',
    },
    table: {
      deal: 'Deal',
      client: 'Client',
      property: 'Property',
      stage: 'Stage',
      amount: 'Amount',
      manager: 'Manager',
      stageTasks: 'Stage tasks',
    },
    list: {
      groupByStage: 'Group by stage',
      stageReadyShort: 'Ready',
      mandatoryShort: 'Req.',
    },
    loading: 'Loading deals...',
    emptyState: 'No deals found',
    emptyStateDescription: 'Create your first deal to get started',
    noDeals: 'No deals',
    export: {
      csv: 'CSV (table)',
      xlsx: 'Excel (XLSX)',
    },
    card: {
      noProperty: 'No property',
      noPrice: 'No amount',
      noClient: 'No client',
      stageReady: 'Ready to advance',
      stageBlocked: 'Mandatory task pending',
      unassigned: 'Unassigned',
      openInNewTab: 'Open in new tab',
    },
    board: {
      ropOnly: 'HoS',
      configurePipeline: 'Configure funnel',
      wipOver: 'Card limit exceeded (WIP)',
      collapseColumn: 'Collapse column',
      expandColumn: 'Expand column',
      noSum: 'No amount set',
      fullscreen: 'Expand to full screen',
      exitFullscreen: 'Collapse',
      scrollToRight: 'Scroll to the right edge',
      scrollToLeft: 'Scroll to the left edge',
    },
    stats: {
      total: 'Total',
      active: 'Active',
      won: 'Won',
      lost: 'Lost',
      allTime: 'all time',
      inWork: '{amount} in progress',
      conversion: 'conversion {percent}%',
    },
    // DEV-42 / DEV-50: 15 stages of the sales funnel.
    stages: {
      STAGE_TYPE_INVALID: 'Invalid stage',
      STAGE_TYPE_UNSORTED: 'Unsorted',
      STAGE_TYPE_NEW_LEAD: 'New Lead',
      STAGE_TYPE_NO_CONTACT: 'No Contact',
      STAGE_TYPE_CONTACTED: 'Contacted',
      STAGE_TYPE_QUALIFIED: 'Qualified',
      STAGE_TYPE_OFFER_SENT: 'Offer Sent',
      STAGE_TYPE_PRESENTATION_SCHEDULED: 'Presentation Scheduled',
      STAGE_TYPE_PRESENTATION_DONE: 'Presentation Done',
      STAGE_TYPE_FOLLOWUP_PRESENTATION_DONE: 'Follow-up Presentation Done',
      STAGE_TYPE_BOOKED: 'Property Selected',
      STAGE_TYPE_DEPOSIT_PAID: 'Deposit Paid',
      STAGE_TYPE_CONTRACT_SIGNED: 'Contract Signed / DP Paid',
      STAGE_TYPE_CONTRACT_HANDED_OVER: 'Contract Handed Over to Developer',
      STAGE_TYPE_COMMISSION_PAID: 'Commission Paid',
      STAGE_TYPE_LOST: 'Closed Lost',
    },
    // DEV-49: close reason reference. Keys come from backend's close_reasons.
    closeReason: {
      AGENT: 'Agent',
      RENTAL_NOT_PURCHASE: 'Rental — no purchase intent',
      BOUGHT_OTHER: 'Bought elsewhere',
      NO_REQUEST_SUBMITTED: "Didn't submit a request",
      NO_CONTACT_5_TOUCHES: 'No contact after 5 touches',
      STOPPED_RESPONDING: 'Stopped responding',
      LANGUAGE_BARRIER: 'Language barrier (no RU/EN)',
      NO_BUDGET: 'No budget',
      NOT_CONSIDERING_VIETNAM: 'Not considering Vietnam',
      INVALID_CONTACTS: 'Invalid contacts',
      NOT_TARGET: 'Not target client',
      DEFERRED_DEMAND: 'Deferred demand',
      PURCHASE_BEYOND_6_MONTHS: 'Purchase 6+ months out',
      CLIENT_ARCHIVED: 'Client archived',
    },
    statuses: {
      DEAL_STATUS_INVALID: 'Invalid',
      DEAL_STATUS_ACTIVE: 'Active',
      DEAL_STATUS_WON: 'Won',
      DEAL_STATUS_LOST: 'Lost',
      DEAL_STATUS_CANCELED: 'Canceled',
    },
    filters: {
      allStatuses: 'All statuses',
      allManagers: 'All managers',
      allAgents: 'All agents',
      managers: 'Managers',
      managerSearch: 'Manager name',
      allComplexes: 'All complexes',
      complexes: 'Complexes',
      complexSearch: 'Complex name',
      taskStateHint:
        'No active tasks — no open tasks at the current stage. Overdue and today — across all tasks of the deal; “Today — all” counts completed ones too',
      taskState: {
        all: 'All tasks',
        none: 'No active tasks',
        overdue: 'Overdue tasks',
        today_all: 'Today — all',
        today: 'Today — in progress',
      },
      allClientSources: 'All channels',
      allLeadSources: 'All lead sources',
      leadSources: 'Lead sources',
      clientPanel: 'Client',
      clientPanelHint:
        "Filters on the deal's client: city, language, tag, archive reason, lead date",
      leadCreated: 'Lead arrived',
      agentSegment: {
        all: 'All deals',
        withAgent: 'With agent',
        withoutAgent: 'Without agent',
      },
      allStages: 'All stages',
      dateRange: 'Creation period',
      reset: 'Reset filters',
      search: 'Search by title',
    },
    detail: {
      totalPrice: 'Total Price',
      commissionPlannedDate: 'Planned commission date',
      finance: {
        title: 'Deal finance',
        formula: 'base = price without VAT × project rate',
        moneyTitle: 'Deal money',
        rewardsTitle: 'Rewards',
        rewardsSubtitle: 'agent and team shares of the base',
        resultTitle: 'Deal result',
        totalWithVat: 'Amount with VAT',
        totalWithoutVat: 'Amount without VAT',
        vatHint: 'VAT {rate}%',
        developerRate: 'Developer · rate',
        developerRateField: 'Developer rate, %',
        editRate: 'Change the developer rate',
        rewardBase: 'Reward base',
        teamGroup: 'Team',
        managerReward: 'Manager',
        hosReward: 'Head of sales',
        editHosRate: 'Change the head-of-sales rate',
        agentReward: 'Agent reward',
        editAgentRate: 'Change the agent rate',
        hosRateField: 'Head-of-sales rate, %',
        agentRateField: 'Agent rate, %',
        supportReward: 'Support manager',
        netRevenue: 'Net revenue',
        shareOfBase: '{percent}% of the base',
        includeInRevenue: 'Counted in operating revenue',
        payments: 'Payments and invoices',
        unitCode: 'Unit code',
        hotBonus: 'Hot bonus',
        adminFee: 'Admin fee',
        rateRange: 'The rate must be between 0 and 100',
        saved: 'Deal finance saved',
        rateConfirm: {
          developerTitle: 'Change the developer rate?',
          hosTitle: 'Change the head-of-sales rate?',
          agentTitle: 'Change the agent rate?',
          description: 'The rate sets the reward base and the net revenue of this deal.',
          impact: 'Now: base {base} · net revenue {net}',
        },
      },
      registry: {
        title: 'Payments and invoices',
        subtitle: 'developer invoices and commission received',
        hint: 'The register line: when the developer invoices, when the money is expected and what has arrived. Saved as a whole — a cleared field clears the value.',
        edit: 'Edit payments',
        plannedInvoiceDate: 'Invoice, planned',
        realInvoiceDate: 'Invoice, actual',
        firstPaymentDate: 'First payment, date',
        firstPaymentAmount: 'First payment, amount',
        firstPayment: 'First payment',
        secondPaymentDate: 'Second payment, date',
        secondPaymentAmount: 'Second payment, amount',
        secondPayment: 'Second payment',
        commissionReceiptDate: 'Commission received, date',
        commissionReceived: 'Commission received',
        downPaymentPlanDate: 'Down payment, planned',
        salesDeptCommission: 'Sales department commission',
        reportNote: 'Report note',
        amountError: 'Amount must be a non-negative number',
        percentError: 'Rate must be between 0 and 100',
      },
      depositPaidAt: 'Deposit date',
      depositDateUpdated: 'Deposit date saved',
      client: 'Client',
      property: 'Residential Complex',
      object: 'Object',
      closeReason: 'Close Reason',
      funnel: 'Sales funnel',
      stageOf: 'stage {current} of {total}',
      nextStage: 'Next: {stage}',
      changeProperty: 'Change',
      selectProperty: 'Select property',
      changePropertyTitle: 'Property',
      propertyUpdated: 'Deal property updated',
      propertyUpdateError: 'Failed to update deal property',
      comments: 'Manager comments',
      summary: 'Summary',
      changeStage: 'Change Stage',
      tasks: 'Tasks',
      addTask: 'Add Task',
      editTask: 'Edit Task',
      noDescription: 'No description',
      taskDescription: 'Description',
      taskDescriptionPlaceholder: 'Task description',
      taskDueDate: 'Due Date',
      taskDueTime: 'Time',
      taskAssignee: 'Assignee',
      mandatoryTask: 'Mandatory',
      optionalTask: 'Optional',
      taskDone: 'Done',
      currentStageTasks: 'Current stage tasks',
      stageTasksNote: 'from stage template',
      manualTasks: 'Added manually',
      manualTasksNote: 'don’t block transition',
      manualTask: 'Manual',
      noDueDate: 'No due date',
      manualAddedAt: 'Added {date}',
      upcomingNote: 'do not block the current transition',
      pastAllDone: 'all closed',
      pastOpenHint:
        'Tasks left over from stages the deal has already passed — close them if they are no longer needed',
      noStageTasks: 'No tasks on this stage',
      funnelBlocked: 'transition blocked',
      tabOverview: 'Overview',
      tabFinance: 'Finance',
      clientAndObject: 'Client and property',
      tabHistory: 'History',
      tabDocuments: 'Documents',
      tabChats: 'Chats',
      loadError: 'Failed to load the deal',
      createdAt: 'created {date}',
      editPrice: 'Edit amount',
      rate: '1 USD ≈ {rate}',
      moreActions: 'Deal actions',
      noHistory: 'No changes yet',
      stageMenu: {
        locked: 'close {n} mandatory',
      },
      deleteConfirm: {
        title: 'Delete deal #{id}?',
        description: 'This cannot be undone',
      },
      priority: {
        high: 'High',
        medium: 'Medium',
        low: 'Low',
      },
      block: {
        blockedTitle: 'Transition to «{stage}» is blocked',
        blockedHint: 'Complete the mandatory stage tasks — manual tasks don’t block the transition',
        closeMandatory:
          'Close {n} mandatory task of the stage | Close {n} mandatory tasks of the stage',
        okTitle: 'All mandatory tasks completed',
        okHint: 'You can move the deal to the next stage',
        mandatory: 'mandatory',
      },
      timer: {
        today: 'Today',
        leftOne: '1 {unit} left',
        left: '{n} {unit} left',
        overdue: 'Overdue by {n} {unit}',
        inTime: 'In {n} {unit}',
        createdDue: 'Created {created} · due {due}',
        day_one: 'day',
        day_few: 'days',
        day_many: 'days',
        hour_one: 'hour',
        hour_few: 'hours',
        hour_many: 'hours',
        task_one: 'task',
        task_few: 'tasks',
        task_many: 'tasks',
      },
      taskAssigneeDefault: 'Deal responsible manager (default)',
      taskAssigneeSupport: '{name} (support manager)',
      taskCalendarHint:
        'A task with a due date will automatically appear in the calendar of the deal’s responsible manager',
      reminderCustomHours: 'N hours before',
      reminderHours: 'Hours before due',
      actionLogs: 'Change History',
      files: 'Files',
      managersHistory: 'Manager History',
      managersHistoryCurrent: 'current',
      managersHistorySince: 'since',
      confirmDeleteTask: 'Delete task?',
      dealDeleted: 'Deal deleted',
      titleUpdated: 'Title updated',
      stageChanged: 'Stage changed',
      priceUpdated: 'Price updated',
      commissionDateUpdated: 'Planned commission date updated',
      agent: {
        model: 'Model',
        special: 'Special',
        rewardPercent: 'Reward, %',
        involvement: 'Involvement',
        involvementStatus: {
          lead_transfer_only: 'Lead transfer only',
          joint_route: 'Joint handling',
          inactive: 'Inactive',
          dispute: 'Dispute',
        },
        expectedRewardDate: 'Expected payout date',
        rewardStatus: 'Reward status',
        rewardStatuses: {
          not_applicable: 'Not applicable',
          expected: 'Expected',
          approved: 'Approved',
          paid: 'Paid',
          disputed: 'Disputed',
          cancelled: 'Cancelled',
        },
        invalidDate: 'Invalid date',
        rewardDateUpdated: 'Payout date updated',
        rewardGroup: 'Agent reward',
        details: 'More',
        commissionBase: 'Commission base',
        expectedReward: 'Expected reward',
        specialTerms: 'Special terms',
        meetings: 'Meetings with agent',
        depositPaidAt: 'Deposit paid',
      },
      updateError: 'Update failed',
      stageChangeError: 'Failed to change stage',
      priceUpdateError: 'Failed to update price',
      addFirstTask: 'Add first task',
      taskAdded: 'Task added',
      taskAddError: 'Failed to add task',
      taskDeleted: 'Task deleted',
      taskDeleteError: 'Failed to delete task',
      taskMarkedComplete: 'Task marked as complete',
      taskReopened: 'Task reopened',
      taskUpdated: 'Task updated',
      tasksCard: {
        loadError: 'Failed to load tasks',
        add: 'Task',
        stage: 'stage «{stage}»',
        pipeline: 'pipeline «{pipeline}»',
        upcoming: 'Next stages tasks ({n})',
        past: 'Tasks from past stages ({n})',
        pastOpen: '{n} open',
        stageGone: 'Stage removed',
        menu: 'Task actions',
      },
      summaryCard: {
        subtitle: 'property and key dates',
        dates: 'Created {created} · updated {updated}',
      },
    },
    form: {
      back: '← Back',
      newDeal: 'New Deal',
      editDeal: 'Edit Deal',
      clientHint: 'Who are we selling to — pick an existing client.',
      objectOptionalHint: 'You can leave the property empty — e.g. for the “Unsorted” stage.',
      wizard: {
        subtitle: 'Create a deal step by step',
        stepsCap: 'STEPS',
        stepOf: 'Step {current} of {total}',
        step1: 'Client',
        step1desc: 'Who we sell to',
        step2: 'Property',
        step2desc: 'Complex & unit · optional',
        step3: 'Amount',
        step3desc: 'Price & terms',
        step4: 'Review',
        step4desc: 'Confirmation',
        skip: 'Skip',
        back: 'Back',
        next: 'Next',
        create: 'Create deal',
        reviewHint:
          'Check the data and click “Create deal”. The deal will appear in the funnel at the selected stage.',
      },
      loadingData: 'Loading deal data...',
      mainInfo: 'Main Information',
      dealTitle: 'Deal Title',
      dealTitlePlaceholder: 'For example: Sale of apartment in the center',
      relations: 'Relations',
      client: 'Client',
      selectClient: 'Select client',
      clientId: 'Client ID',
      clientIdPlaceholder: 'Client ID',
      responsibleManager: 'Responsible Manager',
      managerIdPlaceholder: 'Manager ID',
      // DEV-699. Deal owner is inherited from the client responsible manager.
      managerAutoAssigned: "Assigned from the client's responsible manager",
      property: 'Property',
      selectProperty: 'Residential Complex',
      selectPropertyPlaceholder: 'Select complex',
      selectObject: 'Object',
      selectObjectPlaceholder: 'Select object',
      propertyId: 'Property ID',
      propertyIdPlaceholder: 'Property ID',
      objectId: 'Object ID',
      objectIdPlaceholder: 'Object ID',
      stageAndFinances: 'Stage & Finances',
      initialStage: 'Initial Stage',
      selectStage: 'Select stage',
      finances: 'Finances',
      currency: 'Currency',
      selectCurrency: 'Select currency',
      willBeSavedVnd: 'Will be saved as {amount}',
      priceWarningComplex: 'Over 10× this project’s prices — check for extra zeros',
      priceWarningCeiling: 'Over ₫50 billion — check for extra zeros',
      totalPrice: 'Total Price',
      priceInCents: 'Price in cents',
      dealStatus: 'Deal Status',
      status: 'Status',
      noChange: 'No change',
      comment: 'Comment',
      additionalInfo: 'Additional Information',
      commentPlaceholder: 'Add a comment to the deal...',
      titleRequired: 'Title is required',
      titleAutoGenerated: 'If empty, will be generated from client name and property name',
      clientIdRequired: 'Client is required',
      saveError: 'Error saving deal',
    },
    actions: {
      // DEV-49: matches the LOST stage label "Closed and Lost".
      cancel: 'Mark lost',
    },
    // DEV-699. Deal team: responsible manager + support managers.
    team: {
      title: 'Deal team',
      responsible: 'Responsible manager',
      supportManagers: 'Support managers',
      noSupportManagers: 'No support managers attached',
      addSupport: 'Add support manager',
      noCandidates: 'No available support managers',
      removeConfirm: 'Detach support manager {name} from this deal?',
      addSuccess: 'Support manager attached',
      addError: 'Failed to attach support manager',
      addErrorIsResponsible:
        'This user is already the responsible manager of the deal. Change the client responsible manager first.',
      removeSuccess: 'Support manager detached',
      removeError: 'Failed to detach support manager',
      candidatesError: 'Failed to load support managers',
      // DEV-715. Responsible manager change (via the client).
      selectManager: 'Select manager',
      responsibleChangeConfirmTitle: 'Change responsible manager?',
      responsibleChangeConfirmDescription: 'The responsible manager will change: {from} → {to}.',
      responsibleChangeConfirmWarning:
        'The responsible manager is changed on the client — all active deals of this client will move to the new manager.',
      responsibleChangeSuccess: 'Responsible manager updated',
      responsibleChangeError: 'Failed to change responsible manager',
      snapshotHint: 'Deal snapshot — fixed at creation, edited on the client card',
      responsibleChangePending:
        'The change was accepted and will be applied within a minute. Refresh the page later.',
    },
    cancelModal: {
      title: 'Cancel Deal',
      subtitle: 'Deal “{title}” will be moved to the “Closed and Lost” stage.',
      reasonLabel: 'Close Reason',
      reasonPlaceholder: 'Select a reason...',
      reasonRequired: 'Close reason is required',
      commentLabel: 'Manager comment',
      commentPlaceholder: 'Additional information (optional)',
      confirmTitle: 'Confirm Action',
      confirmCancel: 'Are you sure you want to cancel this deal?',
      confirmButton: 'Yes, Cancel',
      cancelSuccess: 'Deal canceled successfully',
      cancelError: 'Error canceling deal',
    },
    kanban: {
      dealMoved: 'Deal moved to "{stage}"',
      moveError: 'Error moving deal',
      cancelForbidden: 'You can only cancel your own deals',
    },
    currencies: {
      CURRENCY_VND: 'VND (Vietnamese Dong)',
      CURRENCY_USD: 'USD (US Dollar)',
      CURRENCY_RUB: 'RUB (Russian Ruble)',
    },
    // DEV-195. Manager comments (1:N) on a deal card.
    comments: {
      title: 'Manager comments',
      empty: 'No comments yet',
      placeholder: 'Write a comment…',
      add: 'Add',
      addSuccess: 'Comment added',
      addError: 'Failed to add comment',
      updateSuccess: 'Comment updated',
      updateError: 'Failed to update comment',
      deleteSuccess: 'Comment deleted',
      deleteError: 'Failed to delete comment',
      confirmDelete: 'Delete this comment?',
      edited: 'edited',
      unknownAuthor: 'Unknown author',
      subtitle: 'newest first · agent comments highlighted',
      agentBadge: 'Agent',
      agentAuthor: 'Agent',
    },
  },
  notifications: {
    title: 'Notifications',
    empty: 'No notifications',
    emptyDesc: 'Notifications about new messages and events will appear here',
    markAllRead: 'Mark all read',
    viewAll: 'All notifications',
    loadMore: 'Load more',
    allMarkedRead: 'Marked as read: {n}',
    time: {
      justNow: 'just now',
      minutesAgo: '{n} min ago',
      hoursAgo: '{n} hr ago',
      daysAgo: '{n} d ago',
    },
    webPush: {
      enableTitle: 'Enable push notifications',
      enableDesc: 'Get notified about new messages right in your browser',
      enable: 'Enable',
      enabled: 'Push notifications enabled',
      denied: 'Notification permission denied. Check your browser settings',
    },
    channels: {
      webpush: 'Web Push',
      telegram: 'Telegram',
    },
  },
  settings: {
    title: 'Personal settings',
    display: {
      title: 'Display',
      language: 'Interface Language',
      languageDesc: 'Select the language for the interface',
      theme: 'Theme',
      themeDesc: 'Select the color scheme for the interface',
      itemsPerPage: 'Items Per Page',
      itemsPerPageDesc: 'Number of items to display in lists',
      showHints: 'Show Hints',
      showHintsDesc: 'Display hints and instructions in the interface',
    },
    languages: {
      ru: 'Русский',
      en: 'English',
      vn: 'Tiếng Việt',
    },
    themes: {
      light: 'Light',
      dark: 'Dark',
      auto: 'Auto (System)',
    },
    notifications: {
      title: 'Notifications',
      groups: {
        deals: 'Deal events',
        tasks: 'Tasks',
        comm: 'Communication',
      },
      types: {
        assigned: 'Assigned as responsible',
        stage: 'Deal stage changed',
        wonlost: 'Deal won/lost',
        newtask: 'New stage task',
        overdue: 'Task overdue',
        digest: 'Daily task digest',
        reminder: 'Calendar reminder',
        comment: 'New comment',
        missed: 'Missed call',
        unread: 'Unread message',
      },
      channels: {
        title: 'Delivery channels',
        push: 'Push (mobile app)',
        webpush: 'Browser push',
        email: 'Email',
        telegram: 'Telegram',
        whatsapp: 'WhatsApp',
      },
      telegram: {
        title: 'Telegram Bot',
        desc: 'Receive notifications about unread messages via Telegram bot',
        linked: 'Linked',
        notLinked: 'Not linked',
        username: 'Telegram account',
        link: 'Link Telegram',
        unlink: 'Unlink',
        linkInstructions: 'Click the link below and send /start to the bot',
        openBot: 'Open bot',
        codeExpires: 'Link is valid for 10 minutes',
        linkCodeError: 'Could not get the bot link. Please try again later.',
        confirmUnlink: 'Unlink Telegram bot? You will stop receiving notifications.',
        unlinkSuccess: 'Telegram bot unlinked',
        linkSuccess: 'Telegram bot linked! Check the bot — it should confirm the link.',
        checking: 'Checking status...',
        waitingLink: 'Waiting for bot connection...',
      },
      // DEV-2320: a second bot only for new-lead notifications.
      telegramLeads: {
        title: 'Telegram bot for leads',
        desc: 'Receive notifications about new leads via a separate Telegram bot',
        confirmUnlink: 'Unlink the leads bot? New lead notifications will go to the main bot.',
        assignedHint: 'A lead assigned to you goes to the leads bot if it is linked',
      },
      whatsapp: {
        title: 'WhatsApp Notifications',
        desc: 'Receive notifications via WhatsApp',
        linked: 'Linked',
        notLinked: 'Not linked',
        phonePlaceholder: 'WhatsApp number',
        save: 'Save number',
        unlink: 'Unlink',
        confirmUnlink: 'Unlink WhatsApp? You will stop receiving notifications.',
      },
      webPush: {
        title: 'Browser Push Notifications',
        desc: 'Receive notifications directly in your browser even when CRM is not in the active tab',
        notSupported: 'Your browser does not support push notifications',
        permissionDenied:
          'Notification permission is denied. Allow notifications in browser settings and reload the page',
        enable: 'Enable push notifications',
        disable: 'Disable push notifications',
        enabled: 'Enabled',
        disabled: 'Disabled',
      },
    },
    actions: {
      reset: 'Reset Settings',
      save: 'Save Changes',
    },
    confirmReset: 'Are you sure you want to reset all settings to default values?',
    saved: 'Settings saved!',
  },
  auth: {
    // DEV-1177. Shown when the role guard kicks an external role (partner) out of the CRM.
    roleNotAllowed:
      'Your account has no access to the CRM. Partner documentation lives at docs.vinprop.vn.',
    // DEV-1775. Login failure reason shown on the form, not as a toast.
    loginErrors: {
      deactivated: 'Your account is deactivated. Please contact your administrator.',
      notActivated:
        'Your account is not activated yet. Open the invitation email and finish signing up.',
      invalidCredentials: 'Invalid login or password.',
      generic: 'Could not sign in. Please try again.',
    },
    // DEV-2119. Google Workspace sign-in: button and refusal reasons.
    google: {
      button: 'Sign in with Google',
      or: 'or with a password',
      finishing: 'Finishing sign-in…',
      errors: {
        unknown_user: {
          title: 'No such account',
          text: 'Google confirmed the account, but there is no user with this address in the system. Contact your administrator — accounts are created in the CRM.',
        },
        foreign_domain: {
          title: 'Address outside the domain',
          text: 'Only a work address can sign in. Personal Google accounts are not allowed.',
        },
        email_not_verified: {
          title: 'Address not verified',
          text: 'Google has not verified the address of this account. Verify it in your Google settings and try again.',
        },
        inactive_user: {
          title: 'Account is disabled',
          text: 'Signing in with it is closed. Contact your administrator.',
        },
        identity_conflict: {
          title: 'Account already linked',
          text: 'This Google account is linked to a different user. Contact your administrator.',
        },
        canceled: {
          title: 'Sign-in not completed',
          text: 'The Google account chooser closed too early. Try again or sign in with a password.',
        },
        code_used: {
          title: 'Link already used',
          text: 'The one-time return link works once and lives for a minute. Please sign in again.',
        },
        failed: {
          title: 'Could not sign in',
          text: 'Something went wrong on the Google sign-in side. Try again or sign in with a password.',
        },
      },
    },
    login: 'Login',
    logout: 'Logout',
    roles: {
      admin: 'Administrator',
      hos: 'Head of Sales',
      manager: 'Manager',
      // DEV-699. Deal support manager.
      support_manager: 'Support manager',
      agent: 'Agent',
      marketer: 'Marketer',
      // DEV-1976. Campaigns, tracked links and landing SEO — nothing beyond.
      marketing_manager: 'Marketing manager',
      user: 'User',
      ADMIN: 'Administrator',
      MANAGER: 'Manager',
      AGENT: 'Agent',
      USER: 'User',
    },
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot Password?',
    rememberMe: 'Remember Me',
    signIn: 'Sign In',
    forgot: {
      title: 'Password recovery',
      hint: 'Enter your e-mail and we will send a password reset link.',
      submit: 'Send',
      sent: 'If this e-mail is registered, we have sent a password reset link to it.',
      backToLogin: 'Back to login',
      invalidEmail: 'Enter a valid e-mail',
    },
    reset: {
      title: 'New password',
      newPassword: 'New password',
      confirmPassword: 'Repeat password',
      submit: 'Save',
      done: 'Password changed. You can sign in with the new password now.',
      tooShort: 'Password must be at least 8 characters',
      mismatch: 'Passwords do not match',
      invalidToken: 'The link is invalid or expired. Request a password reset again.',
    },
    // DEV-1308. Экран из письма-приглашения.
    accept: {
      title: 'Invitation to VINPROP',
      hint: 'Choose a password — the account will be created for',
      firstName: 'First name',
      lastName: 'Last name',
      password: 'Password',
      confirmPassword: 'Repeat password',
      submit: 'Create account',
      required: 'This field is required',
      invalidToken: 'The invitation is invalid or expired. Ask to send the invitation again.',
      failed: 'Could not accept the invitation. Try again or ask for a new one.',
    },
  },
  languages: {
    ru: 'Русский',
    en: 'English',
    vn: 'Tiếng Việt',
  },
  countries: {
    by: 'Belarus',
    cn: 'China',
    fr: 'France',
    de: 'Germany',
    in: 'India',
    id: 'Indonesia',
    jp: 'Japan',
    kz: 'Kazakhstan',
    my: 'Malaysia',
    ph: 'Philippines',
    ru: 'Russia',
    sa: 'Saudi Arabia',
    sg: 'Singapore',
    kr: 'South Korea',
    th: 'Thailand',
    tr: 'Turkey',
    ua: 'Ukraine',
    ae: 'United Arab Emirates',
    gb: 'United Kingdom',
    us: 'United States',
    vn: 'Vietnam',
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome to VINPROP CRM',
    quickActions: 'Quick Actions',
    stats: {
      totalClients: 'Total Clients',
      activeDeals: 'Active Deals',
      properties: 'Properties',
      leadToDeal: 'Lead → deal',
      leadToDealCaption:
        '{converted} of {total} leads | {converted} of {total} lead | {converted} of {total} leads',
      noLeads: 'No leads',
      dealToWon: 'Deal → commission',
      dealToWonCaption:
        '{converted} of {total} closed | {converted} of {total} closed | {converted} of {total} closed',
      noClosedDeals: 'No closed deals',
      newClients: 'New Clients',
      newDeals: 'New Deals',
      newProperties: 'New Properties',
      growth: '+{value}% this {period}',
      newThisMonth: '{count} new this month',
      stable: 'Stable',
      growing: 'Growing',
      declining: 'Declining',
    },
    periods: {
      day: 'day',
      week: 'week',
      month: 'month',
      year: 'year',
    },
    actions: {
      manageClients: 'Manage Clients',
      viewDeals: 'View Deals',
      browseProperties: 'Browse Properties',
    },
    hero: {
      greeting: 'Welcome back, {name}',
      summary: 'Your CRM summary',
    },
    range: {
      today: 'Today',
      week: 'Week',
      month: 'Month',
      all: 'All',
    },
    window: {
      since: 'Since {date}',
      all: 'All time',
      deltaHint: 'Growth against the same stretch of the previous period',
    },
    degraded:
      'Incomplete data: analytics is unavailable, services counted these by their own rules',
    dealsDynamics: {
      title: 'Deal dynamics',
      subtitle: 'New deals by month, pick the period here',
      legend: 'Deals',
      period: '6 months',
      empty: 'No data',
      monthsShort: '{m} mo',
    },
    comingSoon: {
      title: 'Coming soon',
      analytics: 'Available once analytics is connected',
    },
    tasks: {
      title: "Today's tasks",
      titleTeam: "Team's tasks for today",
      overdue: 'Overdue: {n}',
      empty: 'No tasks for today',
      loadError: 'Could not load tasks',
      allDay: 'All day',
      add: 'Add task',
      more: '{n} more tasks | {n} more task | {n} more tasks',
      collapse: 'Collapse',
    },
    recentClients: {
      title: 'Recent clients',
      viewAll: 'View all',
      colClient: 'Client',
      colSource: 'Lead source',
      colManager: 'Manager',
      colCity: 'City',
      colCreated: 'Added',
      empty: 'No clients yet',
    },
    leadSources: {
      title: 'Lead sources',
      unit: 'clients',
      archived: 'Archived',
      empty: 'No source data',
      unavailable: 'Sources for this period are unavailable, try “All”',
    },
  },
  calendar: {
    title: 'Calendar',
    createEvent: 'Create Event',
    editEvent: 'Edit Event',
    deleteEvent: 'Delete Event',
    today: 'Today',
    more: 'more',
    moreTasks: 'tasks | task | tasks',
    allDay: 'All day',
    noEvents: 'No events',
    views: {
      month: 'Month',
      week: 'Week',
      day: 'Day',
      agenda: 'Agenda',
    },
    agenda: {
      empty: 'No events this month',
    },
    weekdays: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    },
    form: {
      title: 'Title',
      titlePlaceholder: 'Enter event title',
      description: 'Description',
      descriptionPlaceholder: 'Enter event description',
      startDate: 'Start Date',
      startTime: 'Start Time',
      endDate: 'End Date',
      endTime: 'End Time',
      allDay: 'All Day',
      entityType: 'Link Type',
      reminder: 'Reminder',
      manager: 'Manager',
    },
    entityTypes: {
      none: 'No link',
      client: 'Client',
      deal: 'Deal',
      clientTask: 'Client Task',
      dealTask: 'Deal Task',
      trackerMeeting: 'Tracker meeting',
    },
    reminders: {
      none: 'No reminder',
      '5min': '5 minutes before',
      '15min': '15 minutes before',
      '30min': '30 minutes before',
      '1hour': '1 hour before',
      '1day': '1 day before',
    },
    addToCalendar: 'Add to Calendar',
    eventCreated: 'Event created',
    eventUpdated: 'Event updated',
    eventDeleted: 'Event deleted',
    legend: {
      title: 'Event Types',
      client: 'Client',
      deal: 'Deal',
      task: 'Task',
    },
    filters: {
      categories: 'Categories',
      client: 'Clients',
      deal: 'Deals',
      task: 'Deal tasks',
      meeting: 'Meetings',
      none: 'No link',
      managers: 'Managers',
      noManagers: 'No managers found',
      taskTypes: 'Task types',
      noTaskTypes: 'No task types yet',
      taskTypesNote:
        'Only tasks of the selected types are shown — entries without a type are hidden.',
    },
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    openInTracker: 'Open in tracker',
    openEntity: {
      deal: 'Open deal',
      client: 'Open client',
    },
  },
  telephony: {
    noCalls: 'No calls',
    callHistory: 'Call History',
    playRecording: 'Play recording',
    transcriptionAndAi: 'Transcription & AI analysis',
    call: {
      endCall: 'End call',
      calling: 'Calling...',
      callTo: 'Call {phone}',
      call: 'Call',
      endCallLabel: 'End',
      callFailed: 'Call failed',
      initiateFailed: 'Failed to initiate call',
    },
    activeCall: {
      ringing: 'Ringing',
      answered: 'On call',
      ended: 'Ended',
      call: 'Call',
      callingEllipsis: 'Ringing...',
      microphone: 'Microphone',
      endCall: 'End',
      generalCall: 'General call',
    },
    sip: {
      connected: 'SIP: connected',
      connecting: 'SIP: connecting...',
      error: 'SIP: error — {error}',
      disconnected: 'SIP: disconnected',
    },
    disposition: {
      answered: 'Answered',
      busy: 'Busy',
      cancel: 'Cancelled',
      noAnswer: 'No answer',
      failed: 'Failed',
      ringing: 'Ringing',
    },
    recording: {
      loading: 'Loading recording...',
      retry: 'Retry',
      pause: 'Pause',
      play: 'Play',
      download: 'Download',
      unavailable: 'Recording unavailable',
      loadError: 'Failed to load recording',
      playError: 'Playback error',
    },
    transcription: {
      unavailable: 'Transcription unavailable',
      inProgress: 'Speech recognition...',
      error: 'Recognition error',
      aiAnalysis: 'AI Analysis',
      nextSteps: 'Next steps',
      title: 'Transcription',
      channel: {
        manager: 'Manager',
        client: 'Client',
        other: 'Channel {n}',
      },
      qualification: {
        hot: '🔥 Hot',
        warm: '🌤 Warm',
        cold: '❄️ Cold',
      },
    },
  },
  messenger: {
    // DEV-2456: номера по QR больше не подключаются, только общий номер.
    noDevice: {
      title: 'Company WhatsApp number is not connected',
      description:
        'WhatsApp in the CRM works only through the company shared number, which is not connected yet',
    },
    selectChat: 'Select a chat',
    searchPlaceholder: 'Search chats...',
    send: 'Send',
    typeMessage: 'Type a message...',
    attachFile: 'Attach file',
    call: 'Call via WhatsApp',
    // DEV-998: read-only cross-manager Telegram chat viewing (ADMIN/CEO/HOS)
    readonly: {
      banner: '👁 Viewing chat — read-only',
      bannerNamed: "👁 Viewing {name}'s chat — read-only",
      // DEV-1835: the merged feed writes only to the support chat, and the client has none
      mergedNoSupport: '👁 Merged feed — read-only: the client has no support chat',
      // DEV-1670: shared WhatsApp number — the client card is visible, but the role cannot write
      sharedNoWrite: '👁 Shared number — read-only: your role cannot message clients',
      // DEV-1675: Zalo OA — the card is visible, the role cannot write
      zaloNoWrite: '👁 Zalo — read-only: your role cannot message clients',
      footer: '🔒 View mode — sending and calls are unavailable',
    },
    // DEV-2297: the support chat opens without marking messages read; writing unlocks after the button
    supportRead: {
      hint: 'Messages are not read yet. To reply, press “Read”',
      button: 'Read',
    },
    crossView: {
      // DEV-1833: the list now holds "Support" alongside managers.
      selectorLabel: 'Chat',
      selfOption: 'You',
      supportOption: 'Support',
      // DEV-2140: another manager's chat, not a contact with that name.
      managerOption: 'Personal chat · {name}',
      // DEV-2227: a foreign personal chat's history appears once its owner opens it
      historyNotLoaded:
        'The chat history will appear once the manager opens this chat in the CRM. New messages show up right away',
    },
    // DEV-1833: "Sending as" chip in the support chat composer
    support: {
      sendAs: 'Sending as: {name}',
      managerOptionSoon: 'Sending on behalf of a manager is coming later',
    },
    // DEV-1835: merged feed of the support chat and managers' personal TG chats
    mergedFeed: {
      toggle: 'Merge chats',
      label: 'Merged: {names}',
      toSupport: '→ to Support',
      toManager: '→ {name}',
      fromManager: '{name} · manager · personal TG',
      // the admin's own chat in the merged feed — "You", as in the feed label
      toSelf: '→ to you',
      fromSelf: 'You · personal TG',
      // chat header subtitle (MSG-15b mockup)
      subtitle: 'Telegram · merged feed: {names}',
      // DEV-2227: a source sits entirely above the loaded part of the feed
      hiddenAbove: 'More messages above: {names}',
    },
    noContact: {
      title: 'No WhatsApp contact',
      description: "Add a WhatsApp number to the client's contacts to view the conversation",
    },
    noChat: 'No conversation with the client yet',
    noChats: 'No chats',
    noMessages: 'No messages',
    newMessages: 'New messages',
    document: 'Document',
    callEvent: {
      // DEV-2443: подсказка на пузыре звонка в ленте.
      openInPanel: 'Open in call history',
      incoming: 'Incoming call',
      outgoing: 'Outgoing call',
      incomingEnded: 'Incoming call',
      outgoingEnded: 'Outgoing call',
      incomingRejected: 'Incoming call declined',
      outgoingRejected: 'Call declined by recipient',
      missed: 'Missed call',
      noAnswer: 'No answer',
      outgoingMissed: 'No answer',
      duration: 'Duration',
      youMissed: "You didn't answer",
      declined: 'Declined',
    },
    addCaption: 'Add a caption...',
    loadMore: 'Load more',
    providers: {
      comingSoon: 'Soon',
    },
    zalo: {
      noOA: {
        title: 'Zalo OA not connected',
        description: 'Ask an administrator to connect the company Zalo OA.',
        // DEV-1681: держателю messengers:manage — куда идти подключать
        manageDescription: 'The company OA is connected in Administration → Messengers.',
        connectButton: 'Go to connection',
      },
      // DEV-1675: a failed lookup is not «no chat»
      loadFailed: 'Could not load the Zalo chat',
      noChat: {
        title: 'No Zalo chat',
        description: 'No conversation with this client via Zalo yet',
      },
      // DEV-1675: manual binding of a Zalo chat to the client (communications:read_all)
      bind: {
        title: 'Link a Zalo chat',
        description: 'Choose the Zalo chat that belongs to this client',
        placeholder: 'Name in Zalo',
        button: 'Link',
        success: 'Zalo chat linked to the client',
        error: 'Could not link the Zalo chat',
      },
      // DEV-2472: a lead from Zalo turned out to be a client already in the CRM
      move: {
        menu: 'This is an existing client',
        title: 'Move the Zalo chat to the client card',
        description:
          'The lead “{lead}” came from Zalo, but the client is already in the CRM. Choose the client card: the Zalo chat moves there.',
        client: 'Client card',
        placeholder: 'Client name or phone',
        impact:
          'The Zalo chat will move to the card “{client}”. The lead “{lead}” will be archived as a duplicate: its active deals will close and its tasks will be removed.',
        impactArchived:
          'The Zalo chat will move to the card “{client}”. The lead “{lead}” is already archived and will stay there.',
        noChat: 'This card has no Zalo chat — nothing to move',
        button: 'Move',
        success: 'The Zalo chat moved to the client card',
        error: 'Could not move the Zalo chat',
      },
    },
    // DEV-2418: the shared WhatsApp Business number — send failures and incoming calls
    whatsapp: {
      sharedLine: 'Company shared number',
      // DEV-1684: подпись РОПа над исходящим общего номера.
      authorHos: '{name} · Head of Sales',
      sendFailed: {
        payment_required:
          'Message not sent: WhatsApp Business has a payment problem. Tell an administrator',
        generic: 'Failed to send the message',
      },
      call: {
        incoming: 'Incoming WhatsApp call',
        openClient: 'Open client card',
        declineHint: 'The caller will hear that the call was declined',
        waiting: 'Also calling — you can answer after this call',
      },
    },
    telegram: {
      comingSoon: {
        title: 'Telegram — Coming Soon',
        description: 'Telegram integration is under development',
      },
      notConnected: {
        title: 'Telegram not connected',
        description: 'Connect your Telegram account to chat with clients right inside the CRM',
        connectButton: 'Connect Telegram',
      },
      // DEV-1712: the status was never read — claiming "not connected" would be a guess.
      statusUnknown: {
        title: 'Telegram state is unknown',
        description:
          'We could not check whether the account is connected. Try again — it may still be online.',
      },
      // DEV-2109: the card works by read_all right even without a personal session
      personalOffline: {
        title: 'Personal Telegram is not connected',
        hint: "Your own chat is unavailable; support and colleagues' chats are open",
      },
      clientChatsFailed: "Couldn't load the client's chats with other managers",
      supportOffline: 'The support account is not connected right now — messages will not go out',
      // DEV-2109: send failure — the bubble used to just vanish
      sendFailed: {
        not_connected: 'Message not sent: Telegram account is not connected',
        unknown_peer: 'Message not sent: the dialog is not opened in Telegram yet',
        flood: 'Telegram asks to wait {seconds}s — message not sent',
        generic: 'Failed to send the message',
      },
      // DEV-1712: a started login, a revoked session and a failed attempt are
      // different states, and none of them means "the account is not connected".
      state: {
        pendingQr: {
          title: 'The Telegram login is unfinished',
          description: 'The QR code was never scanned. That attempt cannot be resumed.',
        },
        passwordNeeded: {
          title: 'The Telegram login is waiting for the 2FA password',
          description: 'The previous attempt cannot be resumed — the login has to start over.',
        },
        revoked: {
          title: 'The Telegram session was revoked',
          description: 'The account stays linked — sign in again with a QR code.',
        },
        failed: {
          title: 'The last connection attempt failed',
          description: 'The Telegram account is not connected. Start the connection over.',
        },
        actions: {
          restart: 'Start over',
          relogin: 'Sign in again',
        },
      },
      login: {
        title: 'Connect Telegram',
        generating: 'Generating QR code…',
        qrAlt: 'Telegram login QR code',
        step1: 'Open Telegram on your phone',
        step2: 'Settings → Devices → Link Desktop Device',
        step3: 'Point your camera at this QR code',
        expiresIn: 'Code refreshes in {seconds}s',
        expired: 'QR code expired',
        refresh: 'Refresh code',
        success: 'Telegram connected',
        connectedAs: 'Signed in as {name}',
        twoFa: {
          description:
            'This account is protected with a cloud password (2FA). Enter it to finish signing in.',
          label: 'Cloud password',
          placeholder: 'Enter password',
          submit: 'Continue',
          wrongPassword: 'Wrong password. Please try again.',
        },
        error: {
          title: 'Could not connect',
          generic: 'Something went wrong. Please try again.',
          // DEV-1712: the session layer stores an error code, the UI picks the text.
          loginExpired: 'The login attempt has expired. Scan the new code.',
          alreadyConnected:
            'The account is already connected — disconnect the current session first.',
          unavailable: 'Telegram is unavailable right now. Please try again later.',
          logoutFailed: 'Could not disconnect the account. Please try again.',
          statusFailed: 'Could not check the account state. Please try again.',
          retry: 'Try again',
        },
      },
      session: {
        account: 'Telegram account',
        allChats: 'All chats',
        disconnect: 'Disconnect',
        confirmDisconnect: 'Disconnect account?',
      },
      status: {
        inactive: 'Not connected',
        pending_qr: 'Waiting for login',
        password_needed: '2FA password required',
        active: 'Online',
        revoked: 'Session revoked',
        error: 'Error',
      },
      typing: 'typing…',
      noChat: {
        title: 'No chat found',
        description:
          'The client has no Telegram username or phone that resolves to a chat, or there is no conversation yet.',
      },
      picker: {
        searchPlaceholder: 'Search chat by name or phone',
        listLabel: 'Telegram chats list',
        emptyTitle: 'No chats found',
        emptyDescription: 'Start a conversation in Telegram and it will show up here',
        bound: 'Linked to this client',
        bind: 'Link',
        bindSuccess: 'Chat linked to client',
        bindError: 'Could not link chat',
      },
      call: {
        start: 'Call',
        panelTitle: 'Calls',
        empty: 'No calls yet',
        loadError: 'Failed to load calls',
        unknownPeer: 'Contact',
        accept: 'Accept',
        decline: 'Decline',
        cancel: 'Cancel',
        mute: 'Mute',
        hangup: 'End',
        today: 'Today',
        expand: 'Expand',
        collapse: 'Collapse',
        close: 'Close',
        outgoingCall: 'Outgoing call',
        incomingCall: 'Incoming call',
        endedTitle: 'Call ended',
        recording: 'Recording',
        transcript: 'Transcript',
        aiSummary: 'AI summary',
        nextSteps: 'Next steps',
        noRecording: 'No recording',
        transcriptPending: 'Transcript is being prepared…',
        transcriptNone: 'Transcript unavailable',
        state: {
          connecting: 'Connecting…',
          ringing: 'Calling…',
          active: 'On call',
          incoming: 'Incoming call',
        },
        recStatus: {
          ready: 'recording ready',
          processing: 'processing recording…',
          failed: 'transcript unavailable',
        },
        qual: {
          hot: 'Hot',
          warm: 'Warm',
          cold: 'Cold',
        },
        speaker: {
          manager: 'Manager',
          client: 'Client',
        },
        error: {
          generic: 'Call failed',
          mic_denied: 'Microphone access denied',
          busy: 'Another call is already active',
          ws_disconnected: 'No connection to server',
          webrtc_error: 'Connection error',
          connection_failed: 'Connection lost',
        },
      },
    },
  },
  agencyBot: {
    title: 'Agent support bot',
    desc: 'Receive agent request notifications with Take / Close / Transfer buttons.',
    connect: 'Connect bot',
    instructions: 'Open the bot and tap Start — the chat links automatically.',
    openBot: 'Open bot',
    codeExpires: 'The link is valid for 15 minutes.',
  },
  profile: {
    title: 'Profile',
    changeAvatar: 'Change avatar',
    telegram: {
      title: 'Telegram account',
      account: 'Telegram account',
      notConnected: 'No Telegram account linked. Connect one to message clients from the CRM.',
      connect: 'Connect Telegram',
      change: 'Change',
      disconnect: 'Disconnect',
      confirmDisconnect: 'Disconnect account?',
    },
    personalInfo: 'Personal Information',
    name: 'Name',
    email: 'Email',
    role: 'Role',
    timezone: 'Timezone',
    timezonePlaceholder: 'Select timezone',
    save: 'Save',
    saved: 'Saved',
    noChanges: 'No changes',
    languages: 'Languages',
    password: {
      title: 'Change password',
      current: 'Current password',
      new: 'New password',
      confirm: 'Repeat new password',
      submit: 'Change password',
      done: 'Password changed',
      tooShort: 'Password must be at least 8 characters',
      mismatch: 'Passwords do not match',
      wrongOld: 'Current password is incorrect',
      failed: 'Failed to change password. Please try again.',
    },
  },
  agentsRegistry: {
    title: 'Agents',
    comingSoon: 'Companies section coming soon (DEV-478)',
    soon: 'soon',
    notAssigned: 'Unassigned',
    privateAgent: 'Private agent',
    searchPlaceholder: 'Search by name, phone, company…',
    tabs: { agents: 'Agents', companies: 'Companies' },
    filters: {
      status: 'Status',
      responsible: 'Responsible',
      registeredRange: 'Registration date',
    },
    columns: {
      fullName: 'Agent name',
      company: 'Company',
      country: 'Country',
      status: 'Status',
      offer: 'Offer',
      model: 'Model',
      clients: 'Clients',
      deals: 'Deals',
      responsible: 'Responsible',
    },
    status: {
      candidate: 'Candidate',
      pending_offer: 'Pending offer',
      new_partner: 'New partner',
      onboarding: 'Onboarding',
      active: 'Active',
      dormant: 'Dormant',
      blocked: 'Blocked',
      archived: 'Archived',
    },
    offer: {
      not_sent: 'Not sent',
      sent: 'Sent',
      accepted: 'Accepted',
      needs_reacceptance: 'Re-accept',
      rejected: 'Rejected',
      suspended: 'Suspended',
    },
    markers: {
      companyUnconfirmed: 'Company not confirmed',
      offerNotAccepted: 'Offer not accepted',
      specialTerms: 'Special terms',
      checkShort: 'check',
    },
    empty: {
      title: 'No agents found',
      withFilters: 'Nothing matches the selected filters',
      noAgents: 'No agents yet',
    },
    actions: {
      addAgent: 'Add agent',
      reset: 'Reset filters',
      addTask: 'Add task',
    },
    agentRole: 'Agent',
    tabs2: {
      overview: 'Overview',
      clients: 'Clients',
      deals: 'Deals',
      reward: 'Reward',
      offer: 'Offer',
      tasks: 'Tasks',
      history: 'History',
    },
    kpi: {
      clientsTransferred: 'Clients transferred',
      transferredNote: 'total for the agent',
      activeDeals: 'Active deals',
      deposits: 'Deposits',
      rewardForecast: 'Reward forecast',
    },
    assignment: {
      auto_matched: 'Auto-matched',
      manual_confirmed: 'Confirmed',
      requires_verification: 'Needs verification',
      private_agent_confirmed: 'Private agent',
      unknown: '—',
    },
    detail: {
      contacts: 'Contacts',
      commercial: 'Commercial terms',
      phone: 'Phone',
      responsible: 'Responsible',
      source: 'Source',
      agentClients: 'Agent clients',
      allClients: 'All clients',
      companyAndSource: 'Company & source',
      confirmation: 'Confirmation',
      coordinator: 'Coordinator',
      client: 'Client',
      date: 'Date',
      deal: 'Deal',
      openDeal: 'Open',
      noClients: 'The agent has no clients yet',
      clientsStageHint: 'Stage and next step come from deals (read-model, DEV-482).',
      offerBlock: 'Offer',
      status: 'Status',
      version: 'Version',
      acceptedAt: 'Accepted at',
      rewardPercent: 'Percent',
      validTo: 'Valid to',
      rewardForecast: 'Reward forecast',
      degraded482: '— (read-model, DEV-482)',
      dealsSoonTitle: 'Client deals',
      dealsSoon:
        'Coming once the read-model is populated (DEV-482) and deals are linked to the agent.',
      tasksSoonTitle: 'Agent tasks',
      tasksSoon: 'Coming in Wave 2 (agent_tasks).',
      notFound: 'Agent not found',
    },
    form: {
      email: 'Email',
      fullName: 'Full name',
      phone: 'Phone',
      company: 'Company',
      companyPlaceholder: 'Select a company',
      responsible: 'Responsible',
      language: 'Language',
      country: 'Country',
      city: 'City',
      comment: 'Comment',
      created: 'Agent created',
      createError: 'Failed to create agent',
    },
  },
  partnerCompanies: {
    title: 'Partner companies',
    searchPlaceholder: 'Search by company name…',
    system: 'System',
    actions: { addCompany: 'Add company' },
    filters: { status: 'Status', type: 'Type' },
    columns: {
      name: 'Name',
      type: 'Type',
      status: 'Status',
      agreement: 'Agreement',
      agents: 'Agents',
      clients: 'Clients',
      model: 'Model',
      responsible: 'Responsible',
    },
    status: {
      new: 'New',
      active: 'Active',
      dormant: 'Dormant',
      blocked: 'Blocked',
      archived: 'Archived',
      requires_verification: 'Needs verification',
    },
    type: {
      agency: 'Agency',
      brokerage: 'Brokerage',
      developer_partner: 'Developer partner',
      private_agent: 'Private agent',
      other: 'Other',
    },
    agreement: {
      not_started: 'Not started',
      sent: 'Sent',
      signed: 'Signed',
      expired: 'Expired',
      suspended: 'Suspended',
      terminated: 'Terminated',
    },
    empty: {
      title: 'No companies found',
      withFilters: 'Nothing matches the selected filters',
      noCompanies: 'No companies yet',
    },
    tabs: {
      overview: 'Overview',
      contacts: 'Contacts & coordinator',
      agents: 'Agents',
      clientsDeals: 'Clients & deals',
      documents: 'Documents',
      reports: 'Reports',
    },
    kpi: {
      agents: 'Agents',
      agentsNote: 'linked to the company',
      clients: 'Clients',
      activeDeals: 'Active deals',
      rewardForecast: 'Reward forecast',
    },
    detail: {
      agreement: 'Partnership agreement',
      responsible: 'Responsible',
      document: 'Document',
      docsSoon: 'Documents coming later.',
      website: 'Website',
      contract: 'Agreement',
      defaultModel: 'Default model',
      companyAgents: 'Company agents',
      agent: 'Agent',
      noAgents: 'The company has no agents yet',
      coordinator: 'Partnership coordinator',
      role: 'Role',
      contractTerms: 'Agreement & terms',
      signedAt: 'Signed at',
      signedShort: 'signed',
      clientsDealsTitle: 'Company clients & deals',
      documentsTitle: 'Company documents',
      reportsTitle: 'Company reports',
      reportsSoon: 'Coming in analytics (Wave 2).',
      notFound: 'Company not found',
    },
    form: {
      name: 'Company name',
      typePlaceholder: 'Select a type',
      modelPlaceholder: 'Not set',
      created: 'Company created',
      createError: 'Failed to create company',
    },
    autocomplete: {
      minChars: 'Type at least 3 characters',
      noResults: 'Nothing found',
    },
  },
  agents: {
    portalTitle: 'Agent portal',
    logout: 'Sign out',
    cabinet: {
      submitClient: 'Submit a client',
      more: 'More',
      menu: {
        sections: 'Sections',
        settings: 'Settings',
        language: 'Language',
        profileSub: 'Offer, model, curator',
        educationSub: '5 VINPROP strategies',
        supportSub: 'Meeting, question, FAQ',
      },
      onboarding: {
        skip: 'Skip',
        back: 'Back',
        start: 'Start',
        next: 'Next',
        welcome: {
          title: 'Welcome to VINPROP',
          text: 'This is your agent cabinet. In 5 steps we will set things up: offer, profile, company — then you can submit clients.',
        },
        offer: {
          title: 'Offer accepted',
          text: 'You accepted the offer at registration — its text and version are always available in “Profile & company”.',
        },
        profile: {
          title: 'Your profile',
          text: 'Contacts, language and timezone are in “Profile & company”. Commercial terms are configured by your VINPROP manager.',
        },
        company: {
          title: 'Partner company',
          text: 'Your company and curator are linked in your profile. For any questions reach out via “Support”.',
        },
        done: {
          title: 'All set!',
          text: 'Your cabinet is ready. Submit clients — we will close the deal and credit your reward.',
          submit: 'Submit a client',
          toCabinet: 'Go to cabinet',
        },
      },
      searchPlaceholder: 'Search clients, deals…',
      soon: 'soon',
      nav: {
        dashboard: 'Dashboard',
        clients: 'My clients',
        deals: 'My deals',
        projects: 'Projects',
        reward: 'Reward',
        education: 'Education',
        profile: 'Profile & company',
        support: 'Support',
      },
      projects: {
        title: 'Projects',
        searchPlaceholder: 'Search by project name…',
        availableCount: 'Available to you: {count}',
        statusOnSale: 'On sale',
        statusHandedOver: 'Handed over',
        details: 'Details',
        submitClient: '+ Submit client',
        emptyTitle: 'No available projects yet',
        emptyText: 'Projects will appear here once a VINPROP manager grants you access.',
        contactSupport: 'Contact support',
        errorTitle: 'Failed to load projects',
        errorText: 'Please try refreshing the page later.',
        about: 'About the project',
        units: 'Units & prices',
        unitType: 'Type',
        unitArea: 'Area',
        unitPrice: 'Price from',
        materials: 'Materials for the client',
        presentation: 'Presentation',
        download: 'Download',
        ctaTitle: 'Work with this project',
        ctaText:
          'Submit a client for this project — a VINPROP manager will close the deal and record your reward.',
      },
      dashboard: {
        greeting: 'Hello, {who}',
        onboardingTitle: 'Complete your profile setup',
        onboardingText: 'Confirm your company, review strategies, and submit your first client.',
        onboardingContinue: 'Continue',
        onboardingHide: 'Hide',
        kpi_clients: 'Clients submitted',
        kpi_deals: 'Active deals',
        kpi_deposits: 'Deposits',
        kpi_reward: 'Expected reward',
        kpiClientsHint: 'total submitted',
        kpiDealsHint: '{n} in deposit',
        kpiDepositsHint: 'deposit paid',
        kpiRewardHint: 'across active deals',
        nextSteps: 'Next steps',
        noTasks: 'No upcoming tasks',
        recentDeals: 'Recent deals',
        allDeals: 'All deals',
        noDeals: 'No deals yet',
      },
      clients: {
        searchPlaceholder: 'Search by name',
        colClient: 'Client',
        colSource: 'Source',
        colStatus: 'Status',
        colNextStep: 'Next step',
        nextStepInline: 'Next step: {step}',
        colDeal: 'Deal',
        dealOpen: 'Open',
        loading: 'Loading…',
        empty: 'No submitted clients yet',
      },
      clientStatus: {
        new: 'New',
        in_progress: 'In progress',
        deposit_paid: 'Deposit',
        won: 'Deal closed',
        closed: 'Closed',
      },
      nextStep: {
        manager_contact: 'Manager will contact',
        manager_in_progress: 'Manager is working',
        deal_closing: 'Deal closing',
        completed: 'Completed',
      },
      deals: {
        searchPlaceholder: 'Search by deal, client',
        selectHint: 'Select a deal to continue',
        colDeal: 'Deal',
        colClient: 'Client',
        colStage: 'Stage',
        colDeposit: 'Deposit',
        colForecast: 'Forecast',
        colPayoutDate: 'Payout date',
        colPayoutStatus: 'Payout status',
        depositYes: 'Paid',
        depositNo: 'No',
        empty: 'No deals yet',
      },
      reward: {
        forecast: 'Expected reward',
        forecastHint: 'across active deals',
        approved: 'Approved',
        approvedHint: 'ready for payout',
        paid: 'Paid',
        paidHint: 'all time',
        empty: 'No rewards yet',
      },
      rewardStatus: {
        expected: 'Expected',
        approved: 'Approved',
        paid: 'Paid',
        unavailable: 'N/A',
      },
      education: {
        strategiesTitle: 'VINPROP investment strategies',
        strategiesSubtitle:
          'Review all 5 strategies before working actively with clients — it helps qualify requests more precisely.',
        watchYoutube: 'watch on YouTube',
        webinarsTitle: 'Webinars',
        webinarsEmpty: 'No scheduled webinars yet',
        register: 'Register',
        registered: 'Registered',
        watchRecording: 'Recording',
        materialsTitle: 'Agent materials',
        materials: {
          presentations: 'Project presentations',
          pricelists: 'Price lists',
          recordings: 'Webinar recordings',
          instructions: 'Guides & policies',
        },
      },
      profile: {
        agentTitle: 'Agent profile',
        agentRole: 'Agent',
        phone: 'Phone',
        email: 'Email',
        whatsapp: 'WhatsApp',
        telegram: 'Telegram',
        location: 'Country / city',
        timezone: 'Time zone',
        language: 'Language',
        lang: { ru: 'Russian', en: 'English', vn: 'Vietnamese' },
        statusValue: {
          active: 'Active',
          pending: 'Pending',
          blocked: 'Blocked',
          inactive: 'Inactive',
        },
        offerTitle: 'Offer & model',
        offerStatus: 'Offer status',
        offerValue: {
          accepted: 'Accepted',
          pending: 'Not accepted',
          sent: 'Sent',
          declined: 'Declined',
        },
        offerVersion: 'Offer version',
        offerAcceptedAt: 'Accepted on',
        modelTitle: 'Reward model',
        modelValue: {
          '30_percent': '30% — client handover',
          '50_percent': '50% — joint handling',
          special_terms: 'Special terms',
        },
        modelDesc: 'Model description',
        companyTitle: 'Partner company',
        company: 'Company',
        coordinator: 'Coordinator',
        coordinatorRole: 'Role',
        partnerManager: 'Partner manager',
        documents: 'Documents',
        offerDoc: 'Agent offer',
        partnerContract: 'Partner contract',
        open: 'Open',
        changeTitle: 'Need to change the model or details?',
        changeText: 'Your VINPROP manager changes contacts and commercial terms. Contact support.',
      },
      companyVerify: {
        title: 'Company under review',
        statusRequires: 'Needs confirmation',
        typed: 'Entered: “{name}”',
        recommended: '{pct}% recommended',
        cta: 'Choose company',
        privateAgent: 'I am a private agent',
        privateAgentHint: 'I work on my own, without an agency.',
        loading: 'Finding your company options…',
        pending: 'We are matching your company — check back shortly.',
        noResult: 'We could not find your company. Contact your curator — we will add it manually.',
        confirmSuccess: 'Company chosen — awaiting manager confirmation.',
        confirmError: 'Could not choose the company. Please try again.',
      },
      support: {
        myTickets: 'My requests',
        noTickets: 'You have no requests yet',
        backToList: 'Back to list',
        threadEmpty: 'No messages yet',
        you: 'You',
        staff: 'VINPROP',
        replyPlaceholder: 'Your reply…',
        send: 'Send',
        otherQuestion: 'Other question',
        questionLabel: 'Your question',
        questionPlaceholder: 'Describe your question in one message…',
        sendQuestion: 'Send question',
        questionSent: 'Question sent — we will reply during business hours.',
        faq: 'FAQ',
        faqItems: {
          fixation: {
            q: 'How is a client fixed to me?',
            a: 'A client is fixed to you for 90 days from handover; their deals count as yours.',
          },
          payout: {
            q: 'When is the reward paid?',
            a: 'After the deal reaches VINPROP commission payout; the estimated date is shown under Reward.',
          },
          duplicate: {
            q: 'What if the client is already in the base?',
            a: 'The system flags a duplicate for review; the manager decides — track the status under My clients.',
          },
          models: {
            q: 'What is the difference between 30% and 50%?',
            a: '30% — you hand over the client, VINPROP runs the deal; 50% — joint handling. The manager sets the model.',
          },
        },
      },
    },
    login: {
      title: 'Sign in to the agent panel',
      subtitle: 'Refer clients, track deals and your commission.',
      email: 'Email',
      password: 'Password',
      submit: 'Sign in',
      forgot: 'Forgot password?',
      noAccount: 'No account?',
      register: 'Register',
      notAgent: 'This account is not an agent account.',
      error: 'Invalid email or password.',
    },
    offer: {
      back: 'Back',
      version: 'Offer version No. {version}',
    },
    register: {
      title: 'Partner registration',
      subtitle: 'Fill in your details to get started. Confirm your email after registration.',
      backHome: 'Home',
      sectionPartner: 'Partner details',
      sectionContacts: 'Contacts',
      sectionSecurity: 'Security',
      stepLanguage: 'Language',
      stepOffer: 'Offer',
      stepProfile: 'Profile',
      language: 'Language',
      email: 'Email',
      password: 'Password',
      passwordHint: 'At least 8 characters',
      confirmPassword: 'Repeat password',
      passwordMismatch: 'Passwords do not match',
      agency: 'Agency',
      fullName: 'Full name',
      phone: 'Phone',
      country: 'Country',
      city: 'City',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      comment: 'Comment',
      offerTitle: 'Public offer',
      offerText: 'I accept the terms of the',
      offerLink: 'public offer',
      personalData: 'I consent to the processing of personal data',
      dataAccuracy: 'I confirm the accuracy of the provided data',
      next: 'Next',
      back: 'Back',
      submit: 'Register',
      hasAccount: 'Already have an account?',
      signIn: 'Sign in',
      successTitle: 'Almost done!',
      successText:
        'We sent a confirmation email to {email}. Follow the link in the email, then sign in.',
    },
    forgot: {
      title: 'Password recovery',
      email: 'Email',
      submit: 'Send link',
      sent: 'If this email is registered, we have sent a password reset link to it.',
      backToLogin: 'Back to sign in',
    },
    reset: {
      title: 'New password',
      password: 'New password',
      confirm: 'Repeat password',
      submit: 'Save password',
      mismatch: 'Passwords do not match.',
      success: 'Password changed. You can sign in now.',
      toLogin: 'Sign in',
    },
    confirm: {
      verifying: 'Confirming email…',
      success: 'Email confirmed! You can sign in now.',
      error: 'Could not confirm email. The link is invalid or expired.',
      toLogin: 'Sign in',
    },
    home: {
      greeting: 'Hello, {name}!',
      agency: 'Agency: {agency}',
      submitClient: 'Register a client',
      description: 'Share a client contact — we will fix them to you for 90 days.',
      menuSubtitle:
        'Choose an action. Every request is logged and routed to the responsible VINPROP staff member.',
      menuAriaLabel: 'Agent actions',
      menu: {
        partnership: {
          title: 'Partnership',
          subtitle: 'Cooperation terms and agency registration',
        },
        registerClient: {
          title: 'Register a client',
          subtitle: 'Share a contact and fix the client to you',
        },
        commission: {
          title: 'Commission',
          subtitle: 'Check the status and payout date for a deal',
        },
        meeting: {
          title: 'Schedule a meeting',
          subtitle: 'Request a meeting with the client on a deal',
        },
        strategies: {
          title: 'Sales strategies',
          subtitle: 'Videos and materials for sales',
        },
        other: {
          title: 'Other question',
          subtitle: 'Ask the responsible staff member',
        },
      },
      connectBot: {
        title: 'Connect the Telegram bot',
        desc: 'The same actions right inside Telegram: deals, commission, meetings and questions.',
        connect: 'Connect the bot',
        instructions:
          'Open the bot and tap “Start” — your cabinet will be linked to your Telegram automatically.',
        openBot: 'Open in Telegram',
        copyLink: 'Copy link',
        copied: 'Copied',
        codeExpires: 'The link is valid for 15 minutes.',
        error: 'Could not get the link. Please try again.',
      },
    },
    partnership: {
      back: 'Home',
      title: 'Partnership with VINPROP',
      registeredTitle: 'You are already registered as a VINPROP partner',
      registeredText: 'Go to the agent cabinet or pick a section from the menu.',
      toCabinet: 'Go to agent cabinet',
      registerClient: 'Register a client',
      myDeals: 'My deals',
    },
    strategies: {
      back: 'Home',
      title: 'Sales strategies',
      subtitle:
        'A set of videos on VINPROP key investment strategies. We recommend watching all 5 before actively working with clients — it helps qualify requests more precisely.',
      watch: 'Watch on YouTube',
      videos: {
        v1: '1. Structural growth strategy',
        v2: '2. Urban cluster capitalization strategy',
        v3: '3. Hotel shortage strategy',
        v4: '4. Capital concentration strategy',
        v5: '5. Lifestyle strategy',
      },
      note: 'After watching you will be able to better identify which strategy fits a specific client and submit higher-quality requests.',
      ask: 'Ask a question about strategies',
      questionLabel: 'Your question',
      questionPlaceholder: 'Describe your question about strategies in one message…',
      submit: 'Send question',
    },
    other: {
      back: 'Home',
      title: 'Other question',
      subtitle:
        'Describe your question in one message. We will forward it to the responsible VINPROP staff member.',
      label: 'Your question',
      placeholder: 'For example: how is client fixation handled if they contacted VINPROP before?',
      note: 'The request will be routed to Agency Support. The backup is the Head of Sales.',
      submit: 'Send question',
    },
    inquiry: {
      success: {
        title: 'Request received',
        subtitle:
          'Forwarded to the responsible VINPROP staff member. We will reply during business hours.',
        ticketLabel: 'Request number',
        typeLabel: 'Type',
        statusLabel: 'Status',
        statusNew: 'New',
        toHome: 'Home',
      },
      types: {
        other: 'Other question',
        strategy_question: 'Sales strategies',
      },
    },
    deals: {
      title: 'My deals',
      subtitle:
        'Deals where you are listed as the agent. The client name is masked for confidentiality.',
      back: 'Home',
      clientLabel: 'Client: {name}',
      empty: {
        title: 'No linked deals yet',
        description: 'Register a client — the deal will appear here once created in the CRM.',
        action: 'Register a client',
      },
      error: {
        title: 'Failed to load deals',
        retry: 'Retry',
      },
    },
    commission: {
      title: 'Commission question',
      subtitle: 'Pick a deal to see the expected commission payout date.',
      back: 'Home',
      selectedDeal: 'Selected deal',
      changeDeal: 'Choose another deal',
      dateLabel: 'Expected commission payout date',
      pendingTitle: 'Payout date is being clarified',
      pendingFallback:
        'The expected payout date for this deal is being clarified. We have forwarded your request to the deal support specialist.',
      note: 'Commission questions are routed to the responsible VINPROP staff member; you can check the status here.',
      error: {
        title: 'Failed to load data',
        retry: 'Retry',
      },
    },
    meeting: {
      title: 'Schedule a meeting',
      subtitle: 'Pick a deal and preferred options — the manager will arrange the meeting.',
      back: 'Home',
      selectedDeal: 'Selected deal',
      changeDeal: 'Choose another deal',
      withWhom: {
        label: 'Who is meeting',
        client: 'Client',
        agent: 'Agent',
        both: 'Client + agent',
      },
      date: 'Date',
      time: 'Time',
      format: {
        label: 'Meeting format',
        zoom: 'Zoom',
        googleMeet: 'Google Meet',
        whatsapp: 'WhatsApp',
        offline: 'Offline',
      },
      comment: 'Comment',
      commentPlaceholder: 'Meeting goal, client questions…',
      submit: 'Send request',
      success: {
        title: 'Request sent',
        subtitle: 'The manager will contact you to confirm the time and format of the meeting.',
        detailsTitle: 'Request details',
        dealLabel: 'Deal',
        formatLabel: 'Format',
        timeLabel: 'Preferred time',
        toHome: 'Home',
      },
      error: {
        title: 'Failed to load data',
        retry: 'Retry',
      },
    },
    submit: {
      back: 'Home',
      cancel: 'Cancel',
      title: 'Client notification',
      subtitle: 'Refer a client — they will be reserved for you in the CRM.',
      clientName: 'Client name',
      clientPhone: 'Phone',
      clientEmail: 'Email',
      clientLanguage: 'Client language',
      clientCountry: 'Country',
      propertyInterest: 'Interest / preferences',
      propertyInterestPlaceholder: 'Budget, area, property type, purchase goal…',
      clientConsent: 'The client consented to processing of personal data',
      dataAccuracy: 'I confirm the accuracy of the provided data',
      submit: 'Refer client',
      resultCreatedTitle: 'Client registered',
      resultDuplicateTitle: 'Already in database',
      resultPendingTitle: 'Request accepted',
      fixationTitle: 'Client reserved for you',
      fixationText:
        'Reservation valid until {date}. During this period any deal with this client belongs to you.',
      toHome: 'Home',
      close: 'Close',
      another: 'Refer another client',
    },
    validation: {
      required: 'Required field',
      email: 'Invalid email',
      phone: 'Invalid phone number',
      selectCountryFirst: 'Select a country first',
      minPassword: 'At least 8 characters',
      consents: 'You must accept all agreements',
    },
    tags: {
      AGENT_LEAD: 'Agent',
      DUPLICATE: 'Duplicate',
    },
  },
  routing: {
    title: 'Inquiry routing',
    subtitle: 'Rules for where agent inquiries are routed and when to escalate.',
    addRule: 'Add rule',
    createTitle: 'New rule',
    editTitle: 'Edit rule',
    deleteTitle: 'Delete rule',
    deleteText: 'Delete routing rule “{type}”? This cannot be undone.',
    saved: 'Rule saved',
    saveError: 'Failed to save the rule',
    deleted: 'Rule deleted',
    empty: 'No routing rules yet',
    primaryKind: {
      role: 'Role',
      user: 'User',
      dealManager: 'Deal manager',
    },
    fields: {
      requestType: 'Inquiry type',
      requestTypePlaceholder: 'commission_question, meeting_request, other…',
      primaryKind: 'Route to',
      primaryValue: 'Value',
      primaryValueHint: 'user_id for user, role key for role; empty for deal manager',
      fallbackRoleKey: 'Fallback role',
      slaMinutes: 'SLA, minutes',
      condition: 'Condition',
      conditionPlaceholder: 'JSON condition (optional)',
      enabled: 'Enabled',
    },
  },
  reports: {
    // DEV-285. Reports section on analytics API.
    title: 'Reports',
    subtitle: 'Sales analytics: sales volume, funnel, lead sources, managers',
    ownScope: 'Only your deals and clients are shown',
    empty: 'No data for the period',
    // DEV-380. Reports section sub-tabs.
    tabs: {
      overview: 'Overview',
      managers: 'Managers',
      sales: 'Sales team',
      salesFinance: 'Deals and revenue',
    },
    sales: {
      subtitle: 'Lead status structure, flow dynamics, source efficiency and agent lead quality',
      periodPlaceholder: 'Report period',
      exportXlsx: 'Export XLSX',
      empty: 'No leads for the period',
      emptyDescription: 'Pick another period or wait for new leads',
      kpiTotal: 'Total leads',
      kpiEffective: 'Still in work',
      kpiNoDialog: 'Closed without dialog',
      structureTitle: 'Lead status structure',
      monthsTitle: 'Monthly dynamics',
      sourcesTitle: 'Source efficiency',
      agentsTitle: 'Agent lead quality',
      colClass: 'Category',
      colLeads: 'Leads',
      colShare: 'Share',
      colMonth: 'Month',
      colDelta: 'Change',
      deltaBaseline: 'Compared to {period} — {leads} leads',
      colPerDay: 'Leads per day',
      colSource: 'Source',
      colActiveWarming: 'Active + warming',
      colDeals: 'Deals',
      colClosed: 'Closed',
      colEffective: 'Effectiveness',
      colAgentLeads: 'Agent leads',
      colInWork: 'In work',
      colQuality: 'Quality',
      colQualityDelta: 'Δ quality',
      colFirstContact: 'First contact',
      colAgency: 'Agency',
      colAgent: 'Agent',
      colContacts: 'Contacts',
      colTerms: 'Terms',
      colMeetings: 'Meetings',
      colFollowUps: 'Follow-ups',
      colGeo: 'Country / city',
      colRecruitStatus: 'Recruitment status',
      colCurator: 'Curator',
      colNote: 'Note',
      journalTitle: 'Agency partner work journal',
      journalManager: 'Curator',
      journalEmpty: 'No agents',
      journalEmptyDescription: 'No agents found for the selected curator',
      insightsTitle: 'Analytical summary',
      insightsRefresh: 'Refresh summary',
      insightsEmpty: 'No conclusions for this period yet',
      insightsGeneratedAt: 'Generated {date}',
      insightsPeriod: 'Period: {period}',
      insightsTimeout:
        'The model did not finish the summary in time. Please try again in a minute.',
      insightsFailed: 'Could not get the summary. Please try again.',
      exportSettings: {
        title: 'Report delivery settings',
        folder: 'Google Drive folder',
        folderPlaceholder: 'https://drive.google.com/drive/folders/...',
        folderHint: 'The folder must live on a shared drive the VINPROP service account can access',
        folderError: 'Folder is not accessible — check the link and sharing',
        schedule: 'Frequency',
        scheduleDay: 'Every day',
        scheduleWeek: 'Every week',
        scheduleMonth: 'Every month',
        window: 'Data window',
        windowAll: 'All time',
        windowMonths: 'Last {n} months',
        enabled: 'Automatic delivery enabled',
        runNow: 'Export now',
        openFile: 'Open file ↗',
        nextRun: 'Next export: {date}',
        saved: 'Settings saved',
        started: 'Export started',
        finished: 'Report exported to Google Drive',
        alreadyRunning: 'An export is already running',
        status: {
          running: 'Exporting',
          success: 'Exported',
          error: 'Failed',
        },
      },
      noSource: 'No source',
      dataQualityTitle: 'Data quality',
      dqClosedButContacted: 'Closed as "no dialog" while a dialog existed',
      dqActiveWithoutDialog: 'Active leads with no dialog at all',
      hints: {
        kpiTotal:
          'All leads created within the selected period, by client card creation date. This is the denominator for every share in the report.',
        kpiEffective:
          'Leads still in work: deals + active + warming. The share in brackets is against all leads of the period. Shows how much of the flow survived.',
        kpiNoDialog:
          'Leads closed with the reason "No contact made". The share in brackets is against all leads of the period. A rising value is a reason to check how fast managers react.',
        structureTitle:
          'Distribution of all leads of the period across categories. The category is computed automatically and cannot be set by hand.',
        monthsTitle:
          'Lead flow by creation month: volume, growth against the previous month and the category breakdown.',
        sourcesTitle:
          'Source comparison: how many leads each one delivered and which share of them is still in work.',
        agentsTitle:
          'Leads brought in by agents: how many of them stay in work. Leads with the "Agent" source are counted — the same as the "Agent" row of the sources table.',
        dataQualityTitle:
          'Consistency check: mismatches between the closing reason and the fact of contact. Non-zero values mean the numbers above are worth re-checking.',
        class:
          'The category is computed by the first matching rule: an open or won deal and the client has been reached → "Deals"; tag "Test" or "Duplicate" → "Test/duplicate"; lead archived or all its deals canceled or lost → "Closed"; tag "Warming" → "Warming"; everything else → "Active".',
        leads: 'Number of leads in this category created within the period.',
        share: 'Share of the category against all leads of the period.',
        month: 'Lead creation month, by client card creation date.',
        delta:
          'Lead count growth against the previous month, in percent. A month cut short by the period boundary is compared with the same stretch of days of the previous month — otherwise half a month would always look like a slump. Hover the value to see the exact baseline.',
        perDay:
          'Average daily flow: leads of the month divided by the days of that month that have passed within the report period. For the current month that is up to and including today, not all of its days.',
        source:
          'Lead source from the reference book. Leads without a source are grouped into the separate "No source" row.',
        sourceLeads: 'How many leads this source delivered within the period.',
        sourceShare: 'Share of the source in the total lead flow of the period.',
        activeWarming:
          'Leads of this source in "Active" and "Warming" — the ones still being worked.',
        deals:
          'Leads of this source with an open or won deal where the client has already been reached.',
        closed: 'Leads of this source that are archived or whose deals were all canceled or lost.',
        effectiveShare:
          'Deals + active + warming divided by all leads of this source. Compares sources by lead quality rather than volume: a small but living flow can beat a massive one.',
        agentLeads: 'Leads of the month with the "Agent" source.',
        inWork:
          'Of those, still in work: deals + active + warming. Closed and test/duplicate are not counted.',
        quality:
          '"In work" divided by "Agent leads" for the month. Shows whether agents bring target clients: 100% means no lead dropped off, a low value means the flow is empty.',
        qualityDelta:
          'Quality change against the previous month, in percentage points — the difference between two percentages, not a percent of a percent.',
        journalTitle:
          'Agency partner recruitment journal: who was onboarded, on what terms and how many meetings were held. The period filter applies to meeting and follow-up counters only — the agent list itself is not cut by it, otherwise agents onboarded earlier but actively worked with right now would disappear from the journal.',
        firstContact:
          'When the agent appeared in the system: self-registration in the cabinet or a card created by a manager.',
        agency:
          'Agency: the confirmed partner company, otherwise the name the agent entered at registration.',
        contacts:
          'All contact channels: phone, WhatsApp, Telegram, Instagram. For some agency partners Instagram is the only working channel.',
        terms:
          'Commercial model: 30% — client handover, 50% — joint handling, «Special» — individual terms (the approved percentage is shown for those).',
        meetings:
          'Meetings within the period: held / scheduled. «Held» = the meeting task is closed.',
        followUps:
          'Follow-up contacts within the period: done / created. Tasks of type «Follow-up» from the agent card.',
        recruitStatus:
          'Recruitment stage: candidate → awaiting offer → new partner → onboarding → active. Dormant, blocked and archived agents stay in the journal so the recruitment history is not lost.',
        insightsTitle:
          'Conclusions drawn by the model from the numbers in this report: lead flow dynamics, strong and weak sources, conversion bottlenecks. The model only sees aggregates — client records are never sent to it. The summary is stored against those exact numbers: as long as they do not change, reopening the tab returns the same conclusions, while "Refresh summary" asks for a new wording.',
        dqClosedButContacted:
          'Leads closed with "No contact made" even though a chat or an answered call exists in the CRM. The reason is wrong, so "Closed without dialog" in the summary is overstated.',
        dqActiveWithoutDialog:
          'Leads in work with neither a chat nor an answered call: the first contact has not happened yet.',
        classes: {
          deal: 'The client has an open or won deal past the "No Contact" stage, i.e. the client has been reached. Canceled and lost deals do not count. This rule is checked first, so tags and the archive no longer affect the category.',
          active:
            'Lead in work: not archived, no "Warming", "Test" or "Duplicate" tags, and there is no deal or it has not moved past the "No Contact" stage.',
          warming: 'Tag "Warming": the client is interested but the decision is postponed.',
          closed:
            'Lead is archived (the closing reason is picked from the reference book on archiving) or all its deals were canceled or lost.',
          test_duplicate:
            'Tag "Test" or "Duplicate" — service cards. They are excluded from effectiveness so they do not skew the statistics.',
        },
      },
      classes: {
        deal: 'Deals',
        active: 'Active',
        warming: 'Warming',
        closed: 'Closed',
        test_duplicate: 'Test/duplicate',
      },
    },
    // DEV-1882. On-screen version of the "Deals and revenue" sheet.
    finance: {
      subtitle:
        'Plan and fact of a month inside the target period, the weekly dynamics and the deals the revenue is made of',
      month: 'Month',
      includeLeadership: 'Include leadership deals',
      exportXlsx: 'Export XLSX',
      noTarget: 'No goal set for this month',
      noTargetHint:
        'Plans and percentages stay at zero until a goal is set in the report settings.',
      rewardsTitle: 'Rewards of the month · {range}',
      monthGroup: 'Month: {range}',
      periodGroup: 'Period: {range}',
      targetLine:
        'Goal {goal} for {range}. The period spans the calendar months the goal touches; plan weeks start with the goal.',
      periodIsMonth: 'There is no goal for this month, so the period is the month itself.',
      agentsTitle: 'Agents in the period · {range}',
      ofTotal: '{part} of {total}',
      weeksTitle: 'Weekly plan and fact',
      dealsTitle: 'Deals summary',
      emptyDeals: 'No deals with a deposit in this period',
      emptyDealsHint:
        'A deal enters the report by its deposit date — check whether it is filled in.',
      week: 'Week',
      dates: 'Dates',
      colPlan: 'Commission plan',
      colRevenue: 'Operating revenue',
      colNetRevenue: 'Net revenue',
      colCumulative: 'Cumulative',
      colDeposits: 'Deposits',
      colClient: 'Client / project',
      colDeposit: 'Deposit',
      colBase: 'Base',
      colAgent: 'Agent',
      colManager: 'Manager',
      colHos: 'Head of sales',
      colSupport: 'Support manager',
      colNet: 'Net',
      colOwner: 'Owner',
      colCounted: 'Counted',
      countedYes: 'Yes',
      countedNo: 'No',
      countedNoLost: 'No — lost',
      countedNoFlag: 'No — flag off',
      countedNoLeadership: 'No — leadership',
      noClient: 'No client',
      kpi: {
        monthRevenue: 'Operating revenue, month',
        monthPlan: 'Plan of the month',
        monthNetRevenue: 'Net revenue, month',
        monthDeals: 'Deals in the month',
        periodRevenue: 'Revenue for the period',
        periodNetRevenue: 'Net revenue for the period',
        avgCommission: 'Average commission',
        excludedDeals: 'Deals not counted',
        periodPlan: 'Plan of the period',
      },
      rewards: {
        agent: 'Agents',
        agentHint: 'Agent rewards on the counted deals of the month.',
        manager: 'Managers',
        managerHint: 'Manager rewards on the counted deals of the month: base × manager rate.',
        hos: 'Head of sales',
        hosHint:
          'Head-of-sales reward on the counted deals of the month: base × head-of-sales rate.',
        support: 'Support managers',
        supportHint:
          'Support manager rewards on the counted deals of the month. Net revenue subtracts them too.',
        ratesFilled: 'Manager rates filled',
        ratesFilledHint:
          'Deals of the month with a manager rate in the working 10–30 % band, out of all counted deals of the month.',
      },
      agents: {
        inWork: 'Agents in work',
        inWorkHint: 'Agents with at least one meeting or follow-up during the period.',
        new: 'New agents',
        newHint: 'Agent profiles created during the period.',
        clients: 'Clients from agents',
        clientsHint: 'Leads that came from agents during the period.',
      },
      hints: {
        monthRevenue:
          'Sum of the VINPROP reward on deals with a deposit this month. Counted are deals with the revenue flag, except lost ones and — while the switch is off — leadership deals.',
        monthPlan:
          'The share of the goal that falls on the days of the month inside the goal, and how much of it is done.',
        netRevenue:
          'Operating revenue minus the rewards of agents, managers, the head of sales and support managers.',
        monthDeals: 'Counted deals with a deposit this month.',
        periodRevenue:
          'Sum of the VINPROP reward on counted deals with a deposit during the whole period.',
        periodPlan:
          'The goal of the period in the report currency at the rate from the settings, and how much of it is done.',
        avgCommission:
          'Revenue of the period divided by the counted deals of the period that have a base.',
        excludedDeals:
          'Deals of the period with a deposit that the report leaves out: lost, with the revenue flag off, and leadership deals while the switch is off. The reason is in the Counted column.',
        colBase: 'VINPROP reward: price without VAT × developer rate.',
        colNet: 'Base − agent − manager − head of sales − support manager.',
        colCounted: 'Whether the deal is in revenue and, if not, why.',
      },
      settings: {
        title: 'Report settings',
        hint: 'Display currency, rate and goals per period. A goal is split across weeks by days, so it is set as a date range.',
        display: 'Display',
        currency: 'Report currency',
        rate: 'USD/VND rate',
        rateHint:
          'The goal is in dollars and the revenue arrives in dong — without a rate they cannot be compared.',
        rateError: 'Rate must be a positive number',
        targets: 'Goals per period',
        noTargets: 'No goals yet',
        period: 'Period',
        periodFrom: 'Period start',
        periodTo: 'Period end',
        goal: 'Goal',
        goalUsd: 'Goal, USD',
        goalError: 'Goal must be a positive number',
        addTarget: 'Add goal',
        saved: 'Report settings saved',
      },
    },
    kpi: {
      salesVolume: 'Sales volume',
      averagePrice: 'Average deal price',
      dealsWon: 'Deals won',
    },
    hints: {
      salesVolume:
        'Sum of property prices (VAT included) in deals won during the period. Not the company commission — see the “Deals and revenue” tab for that.',
      averagePrice: 'Sales volume for the period divided by the number of deals won.',
      dealsWon: 'Deals moved to “Won” during the period, by closing date.',
      leadToDeal:
        'Of the clients who arrived in the period, how many have a deal that reached the contacted stage (even if lost later). Test clients and duplicates are excluded.',
      dealToWon:
        'Of the deals closed in the period: won against won plus lost after contact. Drop-offs before contact are not losses.',
    },
    revenue: {
      title: 'Sales volume trend',
      hintDays: 'Sum of property prices in won deals, by day won',
      hintMonths: 'Sum of property prices in won deals, by month won',
    },
    funnel: {
      title: 'Sales funnel',
      hint: 'Deals created in the period and how many of them reached each stage or further',
      dealsReached: 'Reached this stage or further',
      fromTop: 'Of those created in the period',
    },
    leadSources: {
      title: 'Lead sources',
      hint: 'Clients who arrived in the period, by lead source; archived ones are excluded',
    },
    leaderboard: {
      title: 'Manager leaderboard',
      hint: 'Deals closed in the period, by responsible manager',
      manager: 'Manager',
      won: 'Won',
      lost: 'Lost',
      winRate: 'Win rate',
      revenue: 'Sales volume',
      averageCheck: 'Avg price',
      clients: 'New clients',
    },
    // DEV-197. Manager analytics (HoS/admin only).
    managerAnalytics: {
      title: 'Manager Analytics',
      subtitle:
        'Stage participation, deal tasks and outcomes for the period. A win or loss counts for the manager who held the deal when it closed; a loss is a cancellation after the client was reached.',
      allManagers: 'All managers',
      period: 'Period',
      dealsWon: 'Deals won',
      dealsLost: 'Lost after contact',
      tasksCompleted: 'Deal tasks completed',
      tasksOverdue: 'Deal tasks overdue',
      tasks: 'Manager tasks',
      winLoss: 'Deal outcomes (Won/Lost)',
      stageParticipation: 'Stage participation',
      empty: 'No data',
      emptyDescription: 'No data for the selected filters',
    },
  },
}
